// Single source of truth for emoji + search keywords, shared by every emoji picker
// in the app (reactions picker in MessageContextMenu/MessageInput, and the
// composer/bubble picker in test-chat-v3). Previously each picker kept its own,
// differently-curated emoji list, so "reactions" and "bubbles/input" showed
// different sets — this file is what both now import from.
//
// `k` is a space-separated bag of search keywords (Russian first, common English
// synonyms after) — e.g. searching "смех" or "ржач" or "laugh" all match 😂.

export interface EmojiItem { v: string; k: string }
export interface EmojiCategory { id: string; label: string; items: EmojiItem[] }

// Shared "quick reactions" row — used by both the reactions long-press menu
// (MessageContextMenu) and the composer/bubble picker's quick row, so the two
// no longer show different top-8 picks.
export const QUICK_REACTIONS = ['👍', '👎', '😂', '😭', '🤣', '😊', '🙏', '🔥'];

export const EMOJI_CATEGORIES: EmojiCategory[] = [
  {
    id: 'smileys', label: 'Смайлы', items: [
      { v: '😀', k: 'улыбка smile grin' },
      { v: '😁', k: 'улыбка зубы grin' },
      { v: '😂', k: 'смех ржач laugh lol' },
      { v: '🤣', k: 'хохот ржака rofl' },
      { v: '😅', k: 'неловко фух sweat smile' },
      { v: '😆', k: 'смех хаха xd' },
      { v: '😊', k: 'улыбка смущение blush' },
      { v: '😇', k: 'ангел невинность angel' },
      { v: '🙂', k: 'улыбка smile' },
      { v: '🙃', k: 'перевернутая ирония upside down' },
      { v: '😉', k: 'подмигиваю wink' },
      { v: '😌', k: 'спокойствие облегчение спокойно relieved' },
      { v: '😍', k: 'влюблен восторг обожаю love heart eyes' },
      { v: '🥰', k: 'любовь нежность in love' },
      { v: '😘', k: 'поцелуй kiss' },
      { v: '😗', k: 'поцелуй kiss' },
      { v: '😙', k: 'поцелуй kiss' },
      { v: '😚', k: 'поцелуй kiss' },
      { v: '😋', k: 'вкусно ням yum' },
      { v: '😛', k: 'язык дразнюсь tongue' },
      { v: '😜', k: 'подмигиваю язык wink tongue' },
      { v: '🤪', k: 'дурачусь псих crazy zany' },
      { v: '😝', k: 'язык дразнюсь squint tongue' },
      { v: '🤑', k: 'деньги жадность money' },
      { v: '🤗', k: 'обнимаю hug' },
      { v: '🤭', k: 'хихикаю giggle' },
      { v: '🫢', k: 'шок рука рот gasp' },
      { v: '🫣', k: 'подглядываю peek' },
      { v: '🤫', k: 'тихо шшш shh' },
      { v: '🤔', k: 'думаю сомневаюсь think' },
      { v: '🫡', k: 'честь салют salute' },
      { v: '🤐', k: 'молчу рот на замке zip' },
      { v: '🤨', k: 'подозрение бровь raised eyebrow' },
      { v: '😐', k: 'нейтрально neutral' },
      { v: '😑', k: 'скучно без выражения expressionless' },
      { v: '😶', k: 'без слов no mouth' },
      { v: '😶‍🌫️', k: 'туман в голове foggy' },
      { v: '😏', k: 'ухмылка смирк smirk' },
      { v: '😒', k: 'недовольство unamused' },
      { v: '🙄', k: 'закатываю глаза rolling eyes' },
      { v: '😬', k: 'гримаса неловко grimace' },
      { v: '🤥', k: 'вру лжец lying nose' },
      { v: '😔', k: 'грусть sad pensive' },
      { v: '😪', k: 'сонный sleepy' },
      { v: '🤤', k: 'слюни drool' },
      { v: '😴', k: 'сон сплю sleep' },
      { v: '😷', k: 'болею маска mask' },
      { v: '🤒', k: 'температура болею thermometer' },
      { v: '🤕', k: 'ушиб травма боль injured hurt' },
      { v: '🤢', k: 'тошнит nauseated' },
      { v: '🤮', k: 'рвота vomit' },
      { v: '🤧', k: 'чихаю sneeze' },
      { v: '🥵', k: 'жарко hot' },
      { v: '🥶', k: 'холодно cold' },
      { v: '🥴', k: 'пьяный головокружение woozy' },
      { v: '😵', k: 'нокаут dizzy' },
      { v: '😵‍💫', k: 'кружится голова dizzy face' },
      { v: '🤯', k: 'взрыв мозга шок mindblown' },
      { v: '🤠', k: 'ковбой cowboy' },
      { v: '🥳', k: 'праздник вечеринка party' },
      { v: '🥸', k: 'маскировка disguise' },
      { v: '😎', k: 'круто очки cool sunglasses' },
      { v: '🤓', k: 'ботан nerd' },
      { v: '🧐', k: 'монокль внимательно curious' },
      { v: '😕', k: 'растерян confused' },
      { v: '😟', k: 'беспокойство worried' },
      { v: '🙁', k: 'грусть frown' },
      { v: '☹️', k: 'грусть frown' },
      { v: '😮', k: 'удивление wow' },
      { v: '😯', k: 'удивление hushed' },
      { v: '😲', k: 'шок astonished' },
      { v: '😳', k: 'смущение flushed' },
      { v: '🥺', k: 'умоляю пожалуйста please puppy eyes' },
      { v: '🥹', k: 'растроган слезы touched' },
      { v: '😦', k: 'испуг frowning' },
      { v: '😧', k: 'страдание anguished' },
      { v: '😨', k: 'испуг fearful' },
      { v: '😰', k: 'тревога anxious sweat' },
      { v: '😥', k: 'грусть облегчение sad relief' },
      { v: '😢', k: 'плачу слезы cry' },
      { v: '😭', k: 'рыдаю плачу sob cry' },
      { v: '😱', k: 'ужас крик scream' },
      { v: '😖', k: 'мучение confounded' },
      { v: '😣', k: 'напряжение persevere' },
      { v: '😞', k: 'разочарован disappointed' },
      { v: '😓', k: 'пот downcast sweat' },
      { v: '😩', k: 'устал weary' },
      { v: '😫', k: 'изможден tired' },
      { v: '🥱', k: 'зевота yawn' },
      { v: '😤', k: 'фыркаю злюсь huff triumph' },
      { v: '😡', k: 'злость ярость angry rage' },
      { v: '😠', k: 'злюсь angry' },
      { v: '🤬', k: 'мат ругаюсь swearing' },
      { v: '😈', k: 'дьявол смайл devil' },
      { v: '👿', k: 'дьявол злой imp' },
      { v: '💀', k: 'смерть скелет skull' },
      { v: '☠️', k: 'череп кости death' },
      { v: '💩', k: 'какашка poop' },
      { v: '🤡', k: 'клоун clown' },
      { v: '👻', k: 'призрак ghost' },
      { v: '👽', k: 'инопланетянин alien' },
      { v: '🫠', k: 'плавлюсь melt' },
      { v: '🫥', k: 'исчезаю невидимка dotted' },
      { v: '🫤', k: 'сомнение diagonal mouth' },
      { v: '😮‍💨', k: 'выдох sigh' },
      { v: '😃', k: 'grinning face with big eyes' },
      { v: '😄', k: 'grinning face with smiling eyes' },
      { v: '🤩', k: 'star-struck' },
      { v: '☺️', k: 'smiling face' },
      { v: '🥲', k: 'smiling face with tear' },
      { v: '🫨', k: 'shaking face' },
      { v: '🙂‍↔️', k: 'head shaking horizontally' },
      { v: '🙂‍↕️', k: 'head shaking vertically' },
      { v: '🫩', k: 'face with bags under eyes' },
      { v: '👹', k: 'ogre' },
      { v: '👺', k: 'goblin' },
      { v: '👾', k: 'alien monster' },
      { v: '🤖', k: 'robot' },
      { v: '😺', k: 'grinning cat' },
      { v: '😸', k: 'grinning cat with smiling eyes' },
      { v: '😹', k: 'cat with tears of joy' },
      { v: '😻', k: 'smiling cat with heart-eyes' },
      { v: '😼', k: 'cat with wry smile' },
      { v: '😽', k: 'kissing cat' },
      { v: '🙀', k: 'weary cat' },
      { v: '😿', k: 'crying cat' },
      { v: '😾', k: 'pouting cat' },
      { v: '💌', k: 'love letter' },
      { v: '🩷', k: 'pink heart' },
      { v: '🩵', k: 'light blue heart' },
      { v: '🩶', k: 'grey heart' },
      { v: '💯', k: 'hundred points' },
      { v: '💢', k: 'anger symbol' },
      { v: '💥', k: 'collision' },
      { v: '💫', k: 'dizzy' },
      { v: '💦', k: 'sweat droplets' },
      { v: '💨', k: 'dashing away' },
      { v: '🕳️', k: 'hole' },
      { v: '💬', k: 'speech balloon' },
      { v: '👁️‍🗨️', k: 'eye in speech bubble' },
      { v: '🗨️', k: 'left speech bubble' },
      { v: '🗯️', k: 'right anger bubble' },
      { v: '💭', k: 'thought balloon' },
      { v: '💤', k: 'ZZZ' },
    ],
  },
  {
    id: 'hearts', label: 'Сердца', items: [
      { v: '❤️', k: 'красное сердце любовь red heart' },
      { v: '🧡', k: 'оранжевое сердце orange heart' },
      { v: '💛', k: 'желтое сердце yellow heart' },
      { v: '💚', k: 'зеленое сердце green heart' },
      { v: '💙', k: 'синее сердце blue heart' },
      { v: '💜', k: 'фиолетовое сердце purple heart' },
      { v: '🖤', k: 'черное сердце black heart' },
      { v: '🤍', k: 'белое сердце white heart' },
      { v: '🤎', k: 'коричневое сердце brown heart' },
      { v: '♥️', k: 'сердце любовь heart' },
      { v: '💔', k: 'разбитое сердце broken heart' },
      { v: '❤️‍🔥', k: 'сердце огонь страсть heart fire' },
      { v: '❤️‍🩹', k: 'заживающее сердце mending heart' },
      { v: '❣️', k: 'сердце восклицание heart exclamation' },
      { v: '💕', k: 'два сердца love hearts' },
      { v: '💞', k: 'сердца вращаются revolving hearts' },
      { v: '💓', k: 'сердце бьется beating heart' },
      { v: '💗', k: 'растущее сердце growing heart' },
      { v: '💖', k: 'сверкающее сердце sparkling heart' },
      { v: '💘', k: 'сердце стрела купидон cupid' },
      { v: '💝', k: 'сердце подарок gift heart' },
      { v: '💟', k: 'сердце украшение heart decoration' },
    ],
  },
  {
    id: 'gestures', label: 'Жесты', items: [
      { v: '👍', k: 'лайк класс да like yes thumbs up' },
      { v: '👎', k: 'дизлайк нет thumbs down' },
      { v: '👌', k: 'окей ok' },
      { v: '✌️', k: 'виктория мир peace victory' },
      { v: '🤞', k: 'удача скрестил пальцы fingers crossed luck' },
      { v: '🤟', k: 'люблю тебя love you' },
      { v: '🤘', k: 'рок metal' },
      { v: '🤙', k: 'позвони мне call me' },
      { v: '👈', k: 'налево point left' },
      { v: '👉', k: 'направо point right' },
      { v: '👆', k: 'вверх point up' },
      { v: '👇', k: 'вниз point down' },
      { v: '☝️', k: 'вверх index up' },
      { v: '✋', k: 'стоп ладонь raised hand stop' },
      { v: '🤚', k: 'ладонь назад raised back hand' },
      { v: '🖐', k: 'пятерня stop hand' },
      { v: '🖖', k: 'вулканский салют spock' },
      { v: '👋', k: 'привет пока wave hello bye' },
      { v: '🤏', k: 'щепотка немного pinch tiny' },
      { v: '💪', k: 'сила бицепс strong muscle' },
      { v: '🙌', k: 'ура celebration yay raise hands' },
      { v: '👐', k: 'открытые ладони open hands' },
      { v: '🤲', k: 'сложенные ладони молю palms up' },
      { v: '🤝', k: 'рукопожатие сделка handshake deal' },
      { v: '🙏', k: 'спасибо молюсь пожалуйста pray thanks please' },
      { v: '✍️', k: 'пишу печатаю записываю записать writing typing' },
      { v: '💅', k: 'маникюр наплевать nails' },
      { v: '🤳', k: 'селфи selfie' },
      { v: '👏', k: 'аплодисменты браво clap bravo' },
    ],
  },
  {
    id: 'people', label: 'Люди', items: [
      { v: '💃', k: 'танцую dance' },
      { v: '🕺', k: 'танцую dance man' },
      { v: '🧑', k: 'человек person' },
      { v: '👶', k: 'малыш baby' },
      { v: '👧', k: 'девочка girl' },
      { v: '🧒', k: 'ребенок child' },
      { v: '👦', k: 'мальчик boy' },
      { v: '👩', k: 'женщина woman' },
      { v: '👨', k: 'мужчина man' },
      { v: '👀', k: 'смотрю глаза eyes look' },
      { v: '👁️', k: 'глаз eye' },
      { v: '👄', k: 'губы lips' },
      { v: '💋', k: 'поцелуй след kiss mark' },
      { v: '🧠', k: 'мозг думаю brain' },
      { v: '🦷', k: 'зуб tooth' },
      { v: '🩸', k: 'кровь капля blood' },
      { v: '🖐️', k: 'hand with fingers splayed' },
      { v: '🫱', k: 'rightwards hand' },
      { v: '🫲', k: 'leftwards hand' },
      { v: '🫳', k: 'palm down hand' },
      { v: '🫴', k: 'palm up hand' },
      { v: '🫷', k: 'leftwards pushing hand' },
      { v: '🫸', k: 'rightwards pushing hand' },
      { v: '🤌', k: 'pinched fingers' },
      { v: '🫰', k: 'hand with index finger and thumb crossed' },
      { v: '🖕', k: 'middle finger' },
      { v: '🫵', k: 'index pointing at the viewer' },
      { v: '✊', k: 'raised fist' },
      { v: '👊', k: 'oncoming fist' },
      { v: '🤛', k: 'left-facing fist' },
      { v: '🤜', k: 'right-facing fist' },
      { v: '🫶', k: 'heart hands' },
      { v: '🦾', k: 'mechanical arm' },
      { v: '🦿', k: 'mechanical leg' },
      { v: '🦵', k: 'leg' },
      { v: '🦶', k: 'foot' },
      { v: '👂', k: 'ear' },
      { v: '🦻', k: 'ear with hearing aid' },
      { v: '👃', k: 'nose' },
      { v: '🫀', k: 'anatomical heart' },
      { v: '🫁', k: 'lungs' },
      { v: '🦴', k: 'bone' },
      { v: '👅', k: 'tongue' },
      { v: '🫦', k: 'biting lip' },
      { v: '👱', k: 'person blond hair' },
      { v: '🧔', k: 'person beard' },
      { v: '🧔‍♂️', k: 'man beard' },
      { v: '🧔‍♀️', k: 'woman beard' },
      { v: '👨‍🦰', k: 'man red hair' },
      { v: '👨‍🦱', k: 'man curly hair' },
      { v: '👨‍🦳', k: 'man white hair' },
      { v: '👨‍🦲', k: 'man bald' },
      { v: '👩‍🦰', k: 'woman red hair' },
      { v: '🧑‍🦰', k: 'person red hair' },
      { v: '👩‍🦱', k: 'woman curly hair' },
      { v: '🧑‍🦱', k: 'person curly hair' },
      { v: '👩‍🦳', k: 'woman white hair' },
      { v: '🧑‍🦳', k: 'person white hair' },
      { v: '👩‍🦲', k: 'woman bald' },
      { v: '🧑‍🦲', k: 'person bald' },
      { v: '👱‍♀️', k: 'woman blond hair' },
      { v: '👱‍♂️', k: 'man blond hair' },
      { v: '🧓', k: 'older person' },
      { v: '👴', k: 'old man' },
      { v: '👵', k: 'old woman' },
      { v: '🙍', k: 'person frowning' },
      { v: '🙍‍♂️', k: 'man frowning' },
      { v: '🙍‍♀️', k: 'woman frowning' },
      { v: '🙎', k: 'person pouting' },
      { v: '🙎‍♂️', k: 'man pouting' },
      { v: '🙎‍♀️', k: 'woman pouting' },
      { v: '🙅', k: 'person gesturing NO' },
      { v: '🙅‍♂️', k: 'man gesturing NO' },
      { v: '🙅‍♀️', k: 'woman gesturing NO' },
      { v: '🙆', k: 'person gesturing OK' },
      { v: '🙆‍♂️', k: 'man gesturing OK' },
      { v: '🙆‍♀️', k: 'woman gesturing OK' },
      { v: '💁', k: 'person tipping hand' },
      { v: '💁‍♂️', k: 'man tipping hand' },
      { v: '💁‍♀️', k: 'woman tipping hand' },
      { v: '🙋', k: 'person raising hand' },
      { v: '🙋‍♂️', k: 'man raising hand' },
      { v: '🙋‍♀️', k: 'woman raising hand' },
      { v: '🧏', k: 'deaf person' },
      { v: '🧏‍♂️', k: 'deaf man' },
      { v: '🧏‍♀️', k: 'deaf woman' },
      { v: '🙇', k: 'person bowing' },
      { v: '🙇‍♂️', k: 'man bowing' },
      { v: '🙇‍♀️', k: 'woman bowing' },
      { v: '🤦', k: 'person facepalming' },
      { v: '🤦‍♂️', k: 'man facepalming' },
      { v: '🤦‍♀️', k: 'woman facepalming' },
      { v: '🤷', k: 'person shrugging' },
      { v: '🤷‍♂️', k: 'man shrugging' },
      { v: '🤷‍♀️', k: 'woman shrugging' },
      { v: '🧑‍⚕️', k: 'health worker' },
      { v: '👨‍⚕️', k: 'man health worker' },
      { v: '👩‍⚕️', k: 'woman health worker' },
      { v: '🧑‍🎓', k: 'student' },
      { v: '👨‍🎓', k: 'man student' },
      { v: '👩‍🎓', k: 'woman student' },
      { v: '🧑‍🏫', k: 'teacher' },
      { v: '👨‍🏫', k: 'man teacher' },
      { v: '👩‍🏫', k: 'woman teacher' },
      { v: '🧑‍⚖️', k: 'judge' },
      { v: '👨‍⚖️', k: 'man judge' },
      { v: '👩‍⚖️', k: 'woman judge' },
      { v: '🧑‍🌾', k: 'farmer' },
      { v: '👨‍🌾', k: 'man farmer' },
      { v: '👩‍🌾', k: 'woman farmer' },
      { v: '🧑‍🍳', k: 'cook' },
      { v: '👨‍🍳', k: 'man cook' },
      { v: '👩‍🍳', k: 'woman cook' },
      { v: '🧑‍🔧', k: 'mechanic' },
      { v: '👨‍🔧', k: 'man mechanic' },
      { v: '👩‍🔧', k: 'woman mechanic' },
      { v: '🧑‍🏭', k: 'factory worker' },
      { v: '👨‍🏭', k: 'man factory worker' },
      { v: '👩‍🏭', k: 'woman factory worker' },
      { v: '🧑‍💼', k: 'office worker' },
      { v: '👨‍💼', k: 'man office worker' },
      { v: '👩‍💼', k: 'woman office worker' },
      { v: '🧑‍🔬', k: 'scientist' },
      { v: '👨‍🔬', k: 'man scientist' },
      { v: '👩‍🔬', k: 'woman scientist' },
      { v: '🧑‍💻', k: 'technologist' },
      { v: '👨‍💻', k: 'man technologist' },
      { v: '👩‍💻', k: 'woman technologist' },
      { v: '🧑‍🎤', k: 'singer' },
      { v: '👨‍🎤', k: 'man singer' },
      { v: '👩‍🎤', k: 'woman singer' },
      { v: '🧑‍🎨', k: 'artist' },
      { v: '👨‍🎨', k: 'man artist' },
      { v: '👩‍🎨', k: 'woman artist' },
      { v: '🧑‍✈️', k: 'pilot' },
      { v: '👨‍✈️', k: 'man pilot' },
      { v: '👩‍✈️', k: 'woman pilot' },
      { v: '🧑‍🚀', k: 'astronaut' },
      { v: '👨‍🚀', k: 'man astronaut' },
      { v: '👩‍🚀', k: 'woman astronaut' },
      { v: '🧑‍🚒', k: 'firefighter' },
      { v: '👨‍🚒', k: 'man firefighter' },
      { v: '👩‍🚒', k: 'woman firefighter' },
      { v: '👮', k: 'police officer' },
      { v: '👮‍♂️', k: 'man police officer' },
      { v: '👮‍♀️', k: 'woman police officer' },
      { v: '🕵️', k: 'detective' },
      { v: '🕵️‍♂️', k: 'man detective' },
      { v: '🕵️‍♀️', k: 'woman detective' },
      { v: '💂', k: 'guard' },
      { v: '💂‍♂️', k: 'man guard' },
      { v: '💂‍♀️', k: 'woman guard' },
      { v: '🥷', k: 'ninja' },
      { v: '👷', k: 'construction worker' },
      { v: '👷‍♂️', k: 'man construction worker' },
      { v: '👷‍♀️', k: 'woman construction worker' },
      { v: '🫅', k: 'person with crown' },
      { v: '🤴', k: 'prince' },
      { v: '👸', k: 'princess' },
      { v: '👳', k: 'person wearing turban' },
      { v: '👳‍♂️', k: 'man wearing turban' },
      { v: '👳‍♀️', k: 'woman wearing turban' },
      { v: '👲', k: 'person with skullcap' },
      { v: '🧕', k: 'woman with headscarf' },
      { v: '🤵', k: 'person in tuxedo' },
      { v: '🤵‍♂️', k: 'man in tuxedo' },
      { v: '🤵‍♀️', k: 'woman in tuxedo' },
      { v: '👰', k: 'person with veil' },
      { v: '👰‍♂️', k: 'man with veil' },
      { v: '👰‍♀️', k: 'woman with veil' },
      { v: '🤰', k: 'pregnant woman' },
      { v: '🫃', k: 'pregnant man' },
      { v: '🫄', k: 'pregnant person' },
      { v: '🤱', k: 'breast-feeding' },
      { v: '👩‍🍼', k: 'woman feeding baby' },
      { v: '👨‍🍼', k: 'man feeding baby' },
      { v: '🧑‍🍼', k: 'person feeding baby' },
      { v: '👼', k: 'baby angel' },
      { v: '🎅', k: 'Santa Claus' },
      { v: '🤶', k: 'Mrs. Claus' },
      { v: '🧑‍🎄', k: 'Mx Claus' },
      { v: '🦸', k: 'superhero' },
      { v: '🦸‍♂️', k: 'man superhero' },
      { v: '🦸‍♀️', k: 'woman superhero' },
      { v: '🦹', k: 'supervillain' },
      { v: '🦹‍♂️', k: 'man supervillain' },
      { v: '🦹‍♀️', k: 'woman supervillain' },
      { v: '🧙', k: 'mage' },
      { v: '🧙‍♂️', k: 'man mage' },
      { v: '🧙‍♀️', k: 'woman mage' },
      { v: '🧚', k: 'fairy' },
      { v: '🧚‍♂️', k: 'man fairy' },
      { v: '🧚‍♀️', k: 'woman fairy' },
      { v: '🧛', k: 'vampire' },
      { v: '🧛‍♂️', k: 'man vampire' },
      { v: '🧛‍♀️', k: 'woman vampire' },
      { v: '🧜', k: 'merperson' },
      { v: '🧜‍♂️', k: 'merman' },
      { v: '🧜‍♀️', k: 'mermaid' },
      { v: '🧝', k: 'elf' },
      { v: '🧝‍♂️', k: 'man elf' },
      { v: '🧝‍♀️', k: 'woman elf' },
      { v: '🧞', k: 'genie' },
      { v: '🧞‍♂️', k: 'man genie' },
      { v: '🧞‍♀️', k: 'woman genie' },
      { v: '🧟', k: 'zombie' },
      { v: '🧟‍♂️', k: 'man zombie' },
      { v: '🧟‍♀️', k: 'woman zombie' },
      { v: '🧌', k: 'troll' },
      { v: '💆', k: 'person getting massage' },
      { v: '💆‍♂️', k: 'man getting massage' },
      { v: '💆‍♀️', k: 'woman getting massage' },
      { v: '💇', k: 'person getting haircut' },
      { v: '💇‍♂️', k: 'man getting haircut' },
      { v: '💇‍♀️', k: 'woman getting haircut' },
      { v: '🚶', k: 'person walking' },
      { v: '🚶‍♂️', k: 'man walking' },
      { v: '🚶‍♀️', k: 'woman walking' },
      { v: '🚶‍➡️', k: 'person walking facing right' },
      { v: '🚶‍♀️‍➡️', k: 'woman walking facing right' },
      { v: '🚶‍♂️‍➡️', k: 'man walking facing right' },
      { v: '🧍', k: 'person standing' },
      { v: '🧍‍♂️', k: 'man standing' },
      { v: '🧍‍♀️', k: 'woman standing' },
      { v: '🧎', k: 'person kneeling' },
      { v: '🧎‍♂️', k: 'man kneeling' },
      { v: '🧎‍♀️', k: 'woman kneeling' },
      { v: '🧎‍➡️', k: 'person kneeling facing right' },
      { v: '🧎‍♀️‍➡️', k: 'woman kneeling facing right' },
      { v: '🧎‍♂️‍➡️', k: 'man kneeling facing right' },
      { v: '🧑‍🦯', k: 'person with white cane' },
      { v: '🧑‍🦯‍➡️', k: 'person with white cane facing right' },
      { v: '👨‍🦯', k: 'man with white cane' },
      { v: '👨‍🦯‍➡️', k: 'man with white cane facing right' },
      { v: '👩‍🦯', k: 'woman with white cane' },
      { v: '👩‍🦯‍➡️', k: 'woman with white cane facing right' },
      { v: '🧑‍🦼', k: 'person in motorized wheelchair' },
      { v: '🧑‍🦼‍➡️', k: 'person in motorized wheelchair facing right' },
      { v: '👨‍🦼', k: 'man in motorized wheelchair' },
      { v: '👨‍🦼‍➡️', k: 'man in motorized wheelchair facing right' },
      { v: '👩‍🦼', k: 'woman in motorized wheelchair' },
      { v: '👩‍🦼‍➡️', k: 'woman in motorized wheelchair facing right' },
      { v: '🧑‍🦽', k: 'person in manual wheelchair' },
      { v: '🧑‍🦽‍➡️', k: 'person in manual wheelchair facing right' },
      { v: '👨‍🦽', k: 'man in manual wheelchair' },
      { v: '👨‍🦽‍➡️', k: 'man in manual wheelchair facing right' },
      { v: '👩‍🦽', k: 'woman in manual wheelchair' },
      { v: '👩‍🦽‍➡️', k: 'woman in manual wheelchair facing right' },
      { v: '🏃', k: 'person running' },
      { v: '🏃‍♂️', k: 'man running' },
      { v: '🏃‍♀️', k: 'woman running' },
      { v: '🏃‍➡️', k: 'person running facing right' },
      { v: '🏃‍♀️‍➡️', k: 'woman running facing right' },
      { v: '🏃‍♂️‍➡️', k: 'man running facing right' },
      { v: '🕴️', k: 'person in suit levitating' },
      { v: '👯', k: 'people with bunny ears' },
      { v: '👯‍♂️', k: 'men with bunny ears' },
      { v: '👯‍♀️', k: 'women with bunny ears' },
      { v: '🧖', k: 'person in steamy room' },
      { v: '🧖‍♂️', k: 'man in steamy room' },
      { v: '🧖‍♀️', k: 'woman in steamy room' },
      { v: '🧗', k: 'person climbing' },
      { v: '🧗‍♂️', k: 'man climbing' },
      { v: '🧗‍♀️', k: 'woman climbing' },
      { v: '🤺', k: 'person fencing' },
      { v: '🏇', k: 'horse racing' },
      { v: '🏌️', k: 'person golfing' },
      { v: '🏌️‍♂️', k: 'man golfing' },
      { v: '🏌️‍♀️', k: 'woman golfing' },
      { v: '🏄', k: 'person surfing' },
      { v: '🏄‍♂️', k: 'man surfing' },
      { v: '🏄‍♀️', k: 'woman surfing' },
      { v: '🚣', k: 'person rowing boat' },
      { v: '🚣‍♂️', k: 'man rowing boat' },
      { v: '🚣‍♀️', k: 'woman rowing boat' },
      { v: '🏊', k: 'person swimming' },
      { v: '🏊‍♂️', k: 'man swimming' },
      { v: '🏊‍♀️', k: 'woman swimming' },
      { v: '⛹️', k: 'person bouncing ball' },
      { v: '⛹️‍♂️', k: 'man bouncing ball' },
      { v: '⛹️‍♀️', k: 'woman bouncing ball' },
      { v: '🏋️‍♂️', k: 'man lifting weights' },
      { v: '🏋️‍♀️', k: 'woman lifting weights' },
      { v: '🚴', k: 'person biking' },
      { v: '🚴‍♂️', k: 'man biking' },
      { v: '🚴‍♀️', k: 'woman biking' },
      { v: '🚵', k: 'person mountain biking' },
      { v: '🚵‍♂️', k: 'man mountain biking' },
      { v: '🚵‍♀️', k: 'woman mountain biking' },
      { v: '🤸', k: 'person cartwheeling' },
      { v: '🤸‍♂️', k: 'man cartwheeling' },
      { v: '🤸‍♀️', k: 'woman cartwheeling' },
      { v: '🤼', k: 'people wrestling' },
      { v: '🤼‍♂️', k: 'men wrestling' },
      { v: '🤼‍♀️', k: 'women wrestling' },
      { v: '🤽', k: 'person playing water polo' },
      { v: '🤽‍♂️', k: 'man playing water polo' },
      { v: '🤽‍♀️', k: 'woman playing water polo' },
      { v: '🤾', k: 'person playing handball' },
      { v: '🤾‍♂️', k: 'man playing handball' },
      { v: '🤾‍♀️', k: 'woman playing handball' },
      { v: '🤹', k: 'person juggling' },
      { v: '🤹‍♂️', k: 'man juggling' },
      { v: '🤹‍♀️', k: 'woman juggling' },
      { v: '🧘', k: 'person in lotus position' },
      { v: '🧘‍♂️', k: 'man in lotus position' },
      { v: '🧘‍♀️', k: 'woman in lotus position' },
      { v: '🛀', k: 'person taking bath' },
      { v: '🛌', k: 'person in bed' },
      { v: '🧑‍🤝‍🧑', k: 'people holding hands' },
      { v: '👭', k: 'women holding hands' },
      { v: '👫', k: 'woman and man holding hands' },
      { v: '👬', k: 'men holding hands' },
      { v: '💏', k: 'kiss' },
      { v: '👩‍❤️‍💋‍👨', k: 'kiss woman, man' },
      { v: '👨‍❤️‍💋‍👨', k: 'kiss man, man' },
      { v: '👩‍❤️‍💋‍👩', k: 'kiss woman, woman' },
      { v: '💑', k: 'couple with heart' },
      { v: '👩‍❤️‍👨', k: 'couple with heart woman, man' },
      { v: '👨‍❤️‍👨', k: 'couple with heart man, man' },
      { v: '👩‍❤️‍👩', k: 'couple with heart woman, woman' },
      { v: '👨‍👩‍👦', k: 'family man, woman, boy' },
      { v: '👨‍👩‍👧', k: 'family man, woman, girl' },
      { v: '👨‍👩‍👧‍👦', k: 'family man, woman, girl, boy' },
      { v: '👨‍👩‍👦‍👦', k: 'family man, woman, boy, boy' },
      { v: '👨‍👩‍👧‍👧', k: 'family man, woman, girl, girl' },
      { v: '👨‍👨‍👦', k: 'family man, man, boy' },
      { v: '👨‍👨‍👧', k: 'family man, man, girl' },
      { v: '👨‍👨‍👧‍👦', k: 'family man, man, girl, boy' },
      { v: '👨‍👨‍👦‍👦', k: 'family man, man, boy, boy' },
      { v: '👨‍👨‍👧‍👧', k: 'family man, man, girl, girl' },
      { v: '👩‍👩‍👦', k: 'family woman, woman, boy' },
      { v: '👩‍👩‍👧', k: 'family woman, woman, girl' },
      { v: '👩‍👩‍👧‍👦', k: 'family woman, woman, girl, boy' },
      { v: '👩‍👩‍👦‍👦', k: 'family woman, woman, boy, boy' },
      { v: '👩‍👩‍👧‍👧', k: 'family woman, woman, girl, girl' },
      { v: '👨‍👦', k: 'family man, boy' },
      { v: '👨‍👦‍👦', k: 'family man, boy, boy' },
      { v: '👨‍👧', k: 'family man, girl' },
      { v: '👨‍👧‍👦', k: 'family man, girl, boy' },
      { v: '👨‍👧‍👧', k: 'family man, girl, girl' },
      { v: '👩‍👦', k: 'family woman, boy' },
      { v: '👩‍👦‍👦', k: 'family woman, boy, boy' },
      { v: '👩‍👧', k: 'family woman, girl' },
      { v: '👩‍👧‍👦', k: 'family woman, girl, boy' },
      { v: '👩‍👧‍👧', k: 'family woman, girl, girl' },
      { v: '🗣️', k: 'speaking head' },
      { v: '👤', k: 'bust in silhouette' },
      { v: '👥', k: 'busts in silhouette' },
      { v: '🫂', k: 'people hugging' },
      { v: '👪', k: 'family' },
      { v: '🧑‍🧑‍🧒', k: 'family adult, adult, child' },
      { v: '🧑‍🧑‍🧒‍🧒', k: 'family adult, adult, child, child' },
      { v: '🧑‍🧒', k: 'family adult, child' },
      { v: '🧑‍🧒‍🧒', k: 'family adult, child, child' },
      { v: '👣', k: 'footprints' },
      { v: '🫆', k: 'fingerprint' },
    ],
  },
  {
    id: 'animals', label: 'Животные', items: [
      { v: '🐶', k: 'собака пес dog' },
      { v: '🐱', k: 'кот кошка cat' },
      { v: '🐭', k: 'мышь mouse' },
      { v: '🐹', k: 'хомяк hamster' },
      { v: '🐰', k: 'кролик заяц rabbit' },
      { v: '🦊', k: 'лиса fox' },
      { v: '🐻', k: 'медведь bear' },
      { v: '🐼', k: 'панда panda' },
      { v: '🐨', k: 'коала koala' },
      { v: '🐯', k: 'тигр tiger' },
      { v: '🦁', k: 'лев lion' },
      { v: '🐮', k: 'корова cow' },
      { v: '🐷', k: 'свинья pig' },
      { v: '🐸', k: 'лягушка frog' },
      { v: '🐵', k: 'обезьяна monkey' },
      { v: '🙈', k: 'обезьяна не вижу see no evil' },
      { v: '🙉', k: 'обезьяна не слышу hear no evil' },
      { v: '🙊', k: 'обезьяна молчу speak no evil' },
      { v: '🐔', k: 'курица chicken' },
      { v: '🐧', k: 'пингвин penguin' },
      { v: '🐦', k: 'птица bird' },
      { v: '🦆', k: 'утка duck' },
      { v: '🦅', k: 'орел eagle' },
      { v: '🦉', k: 'сова owl' },
      { v: '🦇', k: 'летучая мышь bat' },
      { v: '🐺', k: 'волк wolf' },
      { v: '🐗', k: 'кабан boar' },
      { v: '🐴', k: 'лошадь horse' },
      { v: '🦄', k: 'единорог unicorn' },
      { v: '🐝', k: 'пчела bee' },
      { v: '🦋', k: 'бабочка butterfly' },
      { v: '🐌', k: 'улитка snail' },
      { v: '🐞', k: 'божья коровка ladybug' },
      { v: '🐜', k: 'муравей ant' },
      { v: '🐒', k: 'monkey' },
      { v: '🦍', k: 'gorilla' },
      { v: '🦧', k: 'orangutan' },
      { v: '🐕', k: 'dog' },
      { v: '🦮', k: 'guide dog' },
      { v: '🐕‍🦺', k: 'service dog' },
      { v: '🐩', k: 'poodle' },
      { v: '🦝', k: 'raccoon' },
      { v: '🐈', k: 'cat' },
      { v: '🐈‍⬛', k: 'black cat' },
      { v: '🐅', k: 'tiger' },
      { v: '🐆', k: 'leopard' },
      { v: '🫎', k: 'moose' },
      { v: '🫏', k: 'donkey' },
      { v: '🐎', k: 'horse' },
      { v: '🦓', k: 'zebra' },
      { v: '🦌', k: 'deer' },
      { v: '🦬', k: 'bison' },
      { v: '🐂', k: 'ox' },
      { v: '🐃', k: 'water buffalo' },
      { v: '🐄', k: 'cow' },
      { v: '🐖', k: 'pig' },
      { v: '🐽', k: 'pig nose' },
      { v: '🐏', k: 'ram' },
      { v: '🐑', k: 'ewe' },
      { v: '🐐', k: 'goat' },
      { v: '🐪', k: 'camel' },
      { v: '🐫', k: 'two-hump camel' },
      { v: '🦙', k: 'llama' },
      { v: '🦒', k: 'giraffe' },
      { v: '🐘', k: 'elephant' },
      { v: '🦣', k: 'mammoth' },
      { v: '🦏', k: 'rhinoceros' },
      { v: '🦛', k: 'hippopotamus' },
      { v: '🐁', k: 'mouse' },
      { v: '🐀', k: 'rat' },
      { v: '🐇', k: 'rabbit' },
      { v: '🐿️', k: 'chipmunk' },
      { v: '🦫', k: 'beaver' },
      { v: '🦔', k: 'hedgehog' },
      { v: '🐻‍❄️', k: 'polar bear' },
      { v: '🦥', k: 'sloth' },
      { v: '🦦', k: 'otter' },
      { v: '🦨', k: 'skunk' },
      { v: '🦘', k: 'kangaroo' },
      { v: '🦡', k: 'badger' },
      { v: '🐾', k: 'paw prints' },
      { v: '🦃', k: 'turkey' },
      { v: '🐓', k: 'rooster' },
      { v: '🐣', k: 'hatching chick' },
      { v: '🐤', k: 'baby chick' },
      { v: '🐥', k: 'front-facing baby chick' },
      { v: '🕊️', k: 'dove' },
      { v: '🦢', k: 'swan' },
      { v: '🦤', k: 'dodo' },
      { v: '🪶', k: 'feather' },
      { v: '🦩', k: 'flamingo' },
      { v: '🦚', k: 'peacock' },
      { v: '🦜', k: 'parrot' },
      { v: '🪽', k: 'wing' },
      { v: '🐦‍⬛', k: 'black bird' },
      { v: '🪿', k: 'goose' },
      { v: '🐦‍🔥', k: 'phoenix' },
      { v: '🐊', k: 'crocodile' },
      { v: '🐢', k: 'turtle' },
      { v: '🦎', k: 'lizard' },
      { v: '🐍', k: 'snake' },
      { v: '🐲', k: 'dragon face' },
      { v: '🐉', k: 'dragon' },
      { v: '🦕', k: 'sauropod' },
      { v: '🦖', k: 'T-Rex' },
      { v: '🐳', k: 'spouting whale' },
      { v: '🐋', k: 'whale' },
      { v: '🐬', k: 'dolphin' },
      { v: '🦭', k: 'seal' },
      { v: '🐟', k: 'fish' },
      { v: '🐠', k: 'tropical fish' },
      { v: '🐡', k: 'blowfish' },
      { v: '🦈', k: 'shark' },
      { v: '🐙', k: 'octopus' },
      { v: '🐚', k: 'spiral shell' },
      { v: '🪸', k: 'coral' },
      { v: '🪼', k: 'jellyfish' },
      { v: '🦀', k: 'crab' },
      { v: '🦞', k: 'lobster' },
      { v: '🦐', k: 'shrimp' },
      { v: '🦑', k: 'squid' },
      { v: '🦪', k: 'oyster' },
      { v: '🐛', k: 'bug' },
      { v: '🪲', k: 'beetle' },
      { v: '🦗', k: 'cricket' },
      { v: '🪳', k: 'cockroach' },
      { v: '🕷️', k: 'spider' },
      { v: '🕸️', k: 'spider web' },
      { v: '🦂', k: 'scorpion' },
      { v: '🦟', k: 'mosquito' },
      { v: '🪰', k: 'fly' },
      { v: '🪱', k: 'worm' },
      { v: '🦠', k: 'microbe' },
      { v: '💐', k: 'bouquet' },
      { v: '🌸', k: 'cherry blossom' },
      { v: '💮', k: 'white flower' },
      { v: '🪷', k: 'lotus' },
      { v: '🏵️', k: 'rosette' },
      { v: '🌹', k: 'rose' },
      { v: '🥀', k: 'wilted flower' },
      { v: '🌺', k: 'hibiscus' },
      { v: '🌻', k: 'sunflower' },
      { v: '🌼', k: 'blossom' },
      { v: '🌷', k: 'tulip' },
      { v: '🪻', k: 'hyacinth' },
      { v: '🌱', k: 'seedling' },
      { v: '🪴', k: 'potted plant' },
      { v: '🌲', k: 'evergreen tree' },
      { v: '🌳', k: 'deciduous tree' },
      { v: '🌴', k: 'palm tree' },
      { v: '🌵', k: 'cactus' },
      { v: '🌾', k: 'sheaf of rice' },
      { v: '🌿', k: 'herb' },
      { v: '☘️', k: 'shamrock' },
      { v: '🍀', k: 'four leaf clover' },
      { v: '🍁', k: 'maple leaf' },
      { v: '🍂', k: 'fallen leaf' },
      { v: '🍃', k: 'leaf fluttering in wind' },
      { v: '🪹', k: 'empty nest' },
      { v: '🪺', k: 'nest with eggs' },
      { v: '🍄', k: 'mushroom' },
      { v: '🪾', k: 'leafless tree' },
    ],
  },
  {
    id: 'food', label: 'Еда', items: [
      { v: '🍏', k: 'яблоко зеленое apple' },
      { v: '🍎', k: 'яблоко красное apple' },
      { v: '🍌', k: 'банан banana' },
      { v: '🍉', k: 'арбуз watermelon' },
      { v: '🍇', k: 'виноград grapes' },
      { v: '🍓', k: 'клубника strawberry' },
      { v: '🍒', k: 'вишня cherry' },
      { v: '🍑', k: 'персик peach' },
      { v: '🍍', k: 'ананас pineapple' },
      { v: '🥝', k: 'киви kiwi' },
      { v: '🍅', k: 'помидор tomato' },
      { v: '🥑', k: 'авокадо avocado' },
      { v: '🌽', k: 'кукуруза corn' },
      { v: '🥕', k: 'морковь carrot' },
      { v: '🥔', k: 'картошка potato' },
      { v: '🍞', k: 'хлеб bread' },
      { v: '🧀', k: 'сыр cheese' },
      { v: '🍳', k: 'яичница egg fried' },
      { v: '🥓', k: 'бекон bacon' },
      { v: '🍗', k: 'курица нога chicken leg' },
      { v: '🌭', k: 'хот-дог hotdog' },
      { v: '🍔', k: 'бургер бургер burger' },
      { v: '🍟', k: 'картошка фри fries' },
      { v: '🍕', k: 'пицца pizza' },
      { v: '🥪', k: 'сэндвич sandwich' },
      { v: '🌮', k: 'тако taco' },
      { v: '🌯', k: 'буррито burrito' },
      { v: '🥗', k: 'салат salad' },
      { v: '🍈', k: 'melon' },
      { v: '🍊', k: 'tangerine' },
      { v: '🍋', k: 'lemon' },
      { v: '🍋‍🟩', k: 'lime' },
      { v: '🥭', k: 'mango' },
      { v: '🍐', k: 'pear' },
      { v: '🫐', k: 'blueberries' },
      { v: '🫒', k: 'olive' },
      { v: '🥥', k: 'coconut' },
      { v: '🍆', k: 'eggplant' },
      { v: '🌶️', k: 'hot pepper' },
      { v: '🫑', k: 'bell pepper' },
      { v: '🥒', k: 'cucumber' },
      { v: '🥬', k: 'leafy green' },
      { v: '🥦', k: 'broccoli' },
      { v: '🧄', k: 'garlic' },
      { v: '🧅', k: 'onion' },
      { v: '🥜', k: 'peanuts' },
      { v: '🫘', k: 'beans' },
      { v: '🌰', k: 'chestnut' },
      { v: '🫚', k: 'ginger root' },
      { v: '🫛', k: 'pea pod' },
      { v: '🍄‍🟫', k: 'brown mushroom' },
      { v: '🫜', k: 'root vegetable' },
      { v: '🥐', k: 'croissant' },
      { v: '🥖', k: 'baguette bread' },
      { v: '🫓', k: 'flatbread' },
      { v: '🥨', k: 'pretzel' },
      { v: '🥯', k: 'bagel' },
      { v: '🥞', k: 'pancakes' },
      { v: '🧇', k: 'waffle' },
      { v: '🍖', k: 'meat on bone' },
      { v: '🥩', k: 'cut of meat' },
      { v: '🫔', k: 'tamale' },
      { v: '🥙', k: 'stuffed flatbread' },
      { v: '🧆', k: 'falafel' },
      { v: '🥚', k: 'egg' },
      { v: '🥘', k: 'shallow pan of food' },
      { v: '🍲', k: 'pot of food' },
      { v: '🫕', k: 'fondue' },
      { v: '🥣', k: 'bowl with spoon' },
      { v: '🍿', k: 'popcorn' },
      { v: '🧈', k: 'butter' },
      { v: '🧂', k: 'salt' },
      { v: '🥫', k: 'canned food' },
      { v: '🍱', k: 'bento box' },
      { v: '🍘', k: 'rice cracker' },
      { v: '🍙', k: 'rice ball' },
      { v: '🍚', k: 'cooked rice' },
      { v: '🍛', k: 'curry rice' },
      { v: '🍜', k: 'steaming bowl' },
      { v: '🍝', k: 'spaghetti' },
      { v: '🍠', k: 'roasted sweet potato' },
      { v: '🍢', k: 'oden' },
      { v: '🍣', k: 'sushi' },
      { v: '🍤', k: 'fried shrimp' },
      { v: '🍥', k: 'fish cake with swirl' },
      { v: '🥮', k: 'moon cake' },
      { v: '🍡', k: 'dango' },
      { v: '🥟', k: 'dumpling' },
      { v: '🥠', k: 'fortune cookie' },
      { v: '🥡', k: 'takeout box' },
      { v: '🍦', k: 'soft ice cream' },
      { v: '🍧', k: 'shaved ice' },
      { v: '🍨', k: 'ice cream' },
      { v: '🍩', k: 'doughnut' },
      { v: '🍪', k: 'cookie' },
      { v: '🎂', k: 'birthday cake' },
      { v: '🍰', k: 'shortcake' },
      { v: '🧁', k: 'cupcake' },
      { v: '🥧', k: 'pie' },
      { v: '🍫', k: 'chocolate bar' },
      { v: '🍬', k: 'candy' },
      { v: '🍭', k: 'lollipop' },
      { v: '🍮', k: 'custard' },
      { v: '🍯', k: 'honey pot' },
      { v: '🍼', k: 'baby bottle' },
      { v: '🥛', k: 'glass of milk' },
      { v: '🫖', k: 'teapot' },
      { v: '🍶', k: 'sake' },
      { v: '🥃', k: 'tumbler glass' },
      { v: '🫗', k: 'pouring liquid' },
      { v: '🧋', k: 'bubble tea' },
      { v: '🧃', k: 'beverage box' },
      { v: '🧉', k: 'mate' },
      { v: '🧊', k: 'ice' },
      { v: '🥢', k: 'chopsticks' },
      { v: '🍽️', k: 'fork and knife with plate' },
      { v: '🍴', k: 'fork and knife' },
      { v: '🥄', k: 'spoon' },
      { v: '🔪', k: 'kitchen knife' },
      { v: '🫙', k: 'jar' },
      { v: '🏺', k: 'amphora' },
    ],
  },
  {
    id: 'drinks', label: 'Напитки', items: [
      { v: '☕', k: 'кофе coffee' },
      { v: '🍵', k: 'чай tea' },
      { v: '🥤', k: 'напиток стакан drink cup' },
      { v: '🍺', k: 'пиво beer' },
      { v: '🍻', k: 'пиво чокаемся cheers beer' },
      { v: '🥂', k: 'бокалы шампанское cheers' },
      { v: '🍷', k: 'вино wine' },
      { v: '🍸', k: 'коктейль cocktail' },
      { v: '🍹', k: 'коктейль тропический tropical drink' },
      { v: '🍾', k: 'шампанское champagne' },
    ],
  },
  {
    id: 'sports', label: 'Спорт', items: [
      { v: '⚽', k: 'футбол soccer ball' },
      { v: '🏀', k: 'баскетбол basketball' },
      { v: '🏈', k: 'американский футбол football' },
      { v: '⚾', k: 'бейсбол baseball' },
      { v: '🎾', k: 'теннис tennis' },
      { v: '🏐', k: 'волейбол volleyball' },
      { v: '🏓', k: 'пинг-понг ping pong' },
      { v: '🏸', k: 'бадминтон badminton' },
      { v: '🥅', k: 'ворота goal' },
      { v: '⛳', k: 'гольф golf' },
      { v: '🎣', k: 'рыбалка fishing' },
      { v: '🥊', k: 'бокс boxing' },
      { v: '🎽', k: 'бег running' },
      { v: '⛷️', k: 'лыжи skiing' },
      { v: '🏂', k: 'сноуборд snowboard' },
      { v: '🏋️', k: 'штанга тренировка weightlifting gym' },
      { v: '🎃', k: 'jack-o-lantern' },
      { v: '🎄', k: 'Christmas tree' },
      { v: '🧨', k: 'firecracker' },
      { v: '🎈', k: 'balloon' },
      { v: '🎉', k: 'party popper' },
      { v: '🎊', k: 'confetti ball' },
      { v: '🎋', k: 'tanabata tree' },
      { v: '🎍', k: 'pine decoration' },
      { v: '🎎', k: 'Japanese dolls' },
      { v: '🎏', k: 'carp streamer' },
      { v: '🎐', k: 'wind chime' },
      { v: '🎑', k: 'moon viewing ceremony' },
      { v: '🧧', k: 'red envelope' },
      { v: '🎀', k: 'ribbon' },
      { v: '🎁', k: 'wrapped gift' },
      { v: '🎗️', k: 'reminder ribbon' },
      { v: '🎟️', k: 'admission tickets' },
      { v: '🎫', k: 'ticket' },
      { v: '🎖️', k: 'military medal' },
      { v: '🏆', k: 'trophy' },
      { v: '🏅', k: 'sports medal' },
      { v: '🥇', k: '1st place medal' },
      { v: '🥈', k: '2nd place medal' },
      { v: '🥉', k: '3rd place medal' },
      { v: '🥎', k: 'softball' },
      { v: '🏉', k: 'rugby football' },
      { v: '🥏', k: 'flying disc' },
      { v: '🎳', k: 'bowling' },
      { v: '🏏', k: 'cricket game' },
      { v: '🏑', k: 'field hockey' },
      { v: '🏒', k: 'ice hockey' },
      { v: '🥍', k: 'lacrosse' },
      { v: '🥋', k: 'martial arts uniform' },
      { v: '⛸️', k: 'ice skate' },
      { v: '🤿', k: 'diving mask' },
      { v: '🎿', k: 'skis' },
      { v: '🛷', k: 'sled' },
      { v: '🥌', k: 'curling stone' },
      { v: '🎯', k: 'bullseye' },
      { v: '🪀', k: 'yo-yo' },
      { v: '🪁', k: 'kite' },
      { v: '🔫', k: 'water pistol' },
      { v: '🎱', k: 'pool 8 ball' },
      { v: '🔮', k: 'crystal ball' },
      { v: '🪄', k: 'magic wand' },
      { v: '🎮', k: 'video game' },
      { v: '🕹️', k: 'joystick' },
      { v: '🎰', k: 'slot machine' },
      { v: '🎲', k: 'game die' },
      { v: '🧩', k: 'puzzle piece' },
      { v: '🧸', k: 'teddy bear' },
      { v: '🪅', k: 'piñata' },
      { v: '🪩', k: 'mirror ball' },
      { v: '🪆', k: 'nesting dolls' },
      { v: '♠️', k: 'spade suit' },
      { v: '♦️', k: 'diamond suit' },
      { v: '♣️', k: 'club suit' },
      { v: '♟️', k: 'chess pawn' },
      { v: '🃏', k: 'joker' },
      { v: '🀄', k: 'mahjong red dragon' },
      { v: '🎴', k: 'flower playing cards' },
      { v: '🎭', k: 'performing arts' },
      { v: '🖼️', k: 'framed picture' },
      { v: '🎨', k: 'artist palette' },
      { v: '🧵', k: 'thread' },
      { v: '🪡', k: 'sewing needle' },
      { v: '🧶', k: 'yarn' },
      { v: '🪢', k: 'knot' },
    ],
  },
  {
    id: 'travel', label: 'Природа и поездки', items: [
      { v: '🌍', k: 'земля мир world globe' },
      { v: '🗺️', k: 'карта map' },
      { v: '🏔️', k: 'горы mountain' },
      { v: '🌋', k: 'вулкан volcano' },
      { v: '🏖️', k: 'пляж beach' },
      { v: '🏝️', k: 'остров island' },
      { v: '🏜️', k: 'пустыня desert' },
      { v: '🏕️', k: 'кемпинг camp' },
      { v: '☀️', k: 'солнце sun' },
      { v: '🌊', k: 'волна море wave' },
      { v: '🔥', k: 'огонь классно fire' },
      { v: '✨', k: 'блеск звезды sparkles' },
      { v: '⭐', k: 'звезда star' },
      { v: '🌈', k: 'радуга rainbow' },
      { v: '🎆', k: 'салют фейерверк fireworks' },
      { v: '🎇', k: 'салют искры sparkler' },
      { v: '🌎', k: 'globe showing Americas' },
      { v: '🌏', k: 'globe showing Asia-Australia' },
      { v: '🌐', k: 'globe with meridians' },
      { v: '🗾', k: 'map of Japan' },
      { v: '🧭', k: 'compass' },
      { v: '⛰️', k: 'mountain' },
      { v: '🗻', k: 'mount fuji' },
      { v: '🏞️', k: 'national park' },
      { v: '🏟️', k: 'stadium' },
      { v: '🏛️', k: 'classical building' },
      { v: '🏗️', k: 'building construction' },
      { v: '🧱', k: 'brick' },
      { v: '🪨', k: 'rock' },
      { v: '🪵', k: 'wood' },
      { v: '🛖', k: 'hut' },
      { v: '🏘️', k: 'houses' },
      { v: '🏚️', k: 'derelict house' },
      { v: '🏠', k: 'house' },
      { v: '🏡', k: 'house with garden' },
      { v: '🏢', k: 'office building' },
      { v: '🏣', k: 'Japanese post office' },
      { v: '🏤', k: 'post office' },
      { v: '🏥', k: 'hospital' },
      { v: '🏦', k: 'bank' },
      { v: '🏨', k: 'hotel' },
      { v: '🏩', k: 'love hotel' },
      { v: '🏪', k: 'convenience store' },
      { v: '🏫', k: 'school' },
      { v: '🏬', k: 'department store' },
      { v: '🏭', k: 'factory' },
      { v: '🏯', k: 'Japanese castle' },
      { v: '🏰', k: 'castle' },
      { v: '💒', k: 'wedding' },
      { v: '🗼', k: 'Tokyo tower' },
      { v: '🗽', k: 'Statue of Liberty' },
      { v: '⛪', k: 'church' },
      { v: '🕌', k: 'mosque' },
      { v: '🛕', k: 'hindu temple' },
      { v: '🕍', k: 'synagogue' },
      { v: '⛩️', k: 'shinto shrine' },
      { v: '🕋', k: 'kaaba' },
      { v: '⛲', k: 'fountain' },
      { v: '⛺', k: 'tent' },
      { v: '🌁', k: 'foggy' },
      { v: '🌃', k: 'night with stars' },
      { v: '🏙️', k: 'cityscape' },
      { v: '🌄', k: 'sunrise over mountains' },
      { v: '🌅', k: 'sunrise' },
      { v: '🌆', k: 'cityscape at dusk' },
      { v: '🌇', k: 'sunset' },
      { v: '🌉', k: 'bridge at night' },
      { v: '♨️', k: 'hot springs' },
      { v: '🎠', k: 'carousel horse' },
      { v: '🛝', k: 'playground slide' },
      { v: '🎡', k: 'ferris wheel' },
      { v: '🎢', k: 'roller coaster' },
      { v: '💈', k: 'barber pole' },
      { v: '🎪', k: 'circus tent' },
      { v: '🚂', k: 'locomotive' },
      { v: '🚃', k: 'railway car' },
      { v: '🚄', k: 'high-speed train' },
      { v: '🚅', k: 'bullet train' },
      { v: '🚇', k: 'metro' },
      { v: '🚈', k: 'light rail' },
      { v: '🚉', k: 'station' },
      { v: '🚊', k: 'tram' },
      { v: '🚝', k: 'monorail' },
      { v: '🚞', k: 'mountain railway' },
      { v: '🚋', k: 'tram car' },
      { v: '🚍', k: 'oncoming bus' },
      { v: '🚎', k: 'trolleybus' },
      { v: '🚐', k: 'minibus' },
      { v: '🚔', k: 'oncoming police car' },
      { v: '🚖', k: 'oncoming taxi' },
      { v: '🚘', k: 'oncoming automobile' },
      { v: '🛻', k: 'pickup truck' },
      { v: '🚛', k: 'articulated lorry' },
      { v: '🚜', k: 'tractor' },
      { v: '🏍️', k: 'motorcycle' },
      { v: '🛵', k: 'motor scooter' },
      { v: '🦽', k: 'manual wheelchair' },
      { v: '🦼', k: 'motorized wheelchair' },
      { v: '🛺', k: 'auto rickshaw' },
      { v: '🛴', k: 'kick scooter' },
      { v: '🛹', k: 'skateboard' },
      { v: '🛼', k: 'roller skate' },
      { v: '🚏', k: 'bus stop' },
      { v: '🛣️', k: 'motorway' },
      { v: '🛤️', k: 'railway track' },
      { v: '🛢️', k: 'oil drum' },
      { v: '⛽', k: 'fuel pump' },
      { v: '🛞', k: 'wheel' },
      { v: '🚨', k: 'police car light' },
      { v: '🚥', k: 'horizontal traffic light' },
      { v: '🚦', k: 'vertical traffic light' },
      { v: '🛑', k: 'stop sign' },
      { v: '🚧', k: 'construction' },
      { v: '⚓', k: 'anchor' },
      { v: '🛟', k: 'ring buoy' },
      { v: '⛵', k: 'sailboat' },
      { v: '🛶', k: 'canoe' },
      { v: '🚤', k: 'speedboat' },
      { v: '🛳️', k: 'passenger ship' },
      { v: '⛴️', k: 'ferry' },
      { v: '🛥️', k: 'motor boat' },
      { v: '🚢', k: 'ship' },
      { v: '🛩️', k: 'small airplane' },
      { v: '🛫', k: 'airplane departure' },
      { v: '🛬', k: 'airplane arrival' },
      { v: '🪂', k: 'parachute' },
      { v: '💺', k: 'seat' },
      { v: '🚁', k: 'helicopter' },
      { v: '🚟', k: 'suspension railway' },
      { v: '🚠', k: 'mountain cableway' },
      { v: '🚡', k: 'aerial tramway' },
      { v: '🛰️', k: 'satellite' },
      { v: '🚀', k: 'rocket' },
      { v: '🛸', k: 'flying saucer' },
      { v: '🛎️', k: 'bellhop bell' },
      { v: '⌛', k: 'hourglass done' },
      { v: '⏳', k: 'hourglass not done' },
      { v: '⌚', k: 'watch' },
      { v: '⏰', k: 'alarm clock' },
      { v: '⏱️', k: 'stopwatch' },
      { v: '⏲️', k: 'timer clock' },
      { v: '🕰️', k: 'mantelpiece clock' },
      { v: '🕛', k: 'twelve o’clock' },
      { v: '🕧', k: 'twelve-thirty' },
      { v: '🕐', k: 'one o’clock' },
      { v: '🕜', k: 'one-thirty' },
      { v: '🕑', k: 'two o’clock' },
      { v: '🕝', k: 'two-thirty' },
      { v: '🕒', k: 'three o’clock' },
      { v: '🕞', k: 'three-thirty' },
      { v: '🕓', k: 'four o’clock' },
      { v: '🕟', k: 'four-thirty' },
      { v: '🕔', k: 'five o’clock' },
      { v: '🕠', k: 'five-thirty' },
      { v: '🕕', k: 'six o’clock' },
      { v: '🕡', k: 'six-thirty' },
      { v: '🕖', k: 'seven o’clock' },
      { v: '🕢', k: 'seven-thirty' },
      { v: '🕗', k: 'eight o’clock' },
      { v: '🕣', k: 'eight-thirty' },
      { v: '🕘', k: 'nine o’clock' },
      { v: '🕤', k: 'nine-thirty' },
      { v: '🕙', k: 'ten o’clock' },
      { v: '🕥', k: 'ten-thirty' },
      { v: '🕚', k: 'eleven o’clock' },
      { v: '🕦', k: 'eleven-thirty' },
      { v: '🌑', k: 'new moon' },
      { v: '🌒', k: 'waxing crescent moon' },
      { v: '🌓', k: 'first quarter moon' },
      { v: '🌔', k: 'waxing gibbous moon' },
      { v: '🌕', k: 'full moon' },
      { v: '🌖', k: 'waning gibbous moon' },
      { v: '🌗', k: 'last quarter moon' },
      { v: '🌘', k: 'waning crescent moon' },
      { v: '🌙', k: 'crescent moon' },
      { v: '🌚', k: 'new moon face' },
      { v: '🌛', k: 'first quarter moon face' },
      { v: '🌜', k: 'last quarter moon face' },
      { v: '🌡️', k: 'thermometer' },
      { v: '🌝', k: 'full moon face' },
      { v: '🌞', k: 'sun with face' },
      { v: '🪐', k: 'ringed planet' },
      { v: '🌟', k: 'glowing star' },
      { v: '🌠', k: 'shooting star' },
      { v: '🌌', k: 'milky way' },
      { v: '☁️', k: 'cloud' },
      { v: '⛅', k: 'sun behind cloud' },
      { v: '⛈️', k: 'cloud with lightning and rain' },
      { v: '🌤️', k: 'sun behind small cloud' },
      { v: '🌥️', k: 'sun behind large cloud' },
      { v: '🌦️', k: 'sun behind rain cloud' },
      { v: '🌧️', k: 'cloud with rain' },
      { v: '🌨️', k: 'cloud with snow' },
      { v: '🌩️', k: 'cloud with lightning' },
      { v: '🌪️', k: 'tornado' },
      { v: '🌫️', k: 'fog' },
      { v: '🌬️', k: 'wind face' },
      { v: '🌀', k: 'cyclone' },
      { v: '🌂', k: 'closed umbrella' },
      { v: '☂️', k: 'umbrella' },
      { v: '☔', k: 'umbrella with rain drops' },
      { v: '⛱️', k: 'umbrella on ground' },
      { v: '⚡', k: 'high voltage' },
      { v: '❄️', k: 'snowflake' },
      { v: '☃️', k: 'snowman' },
      { v: '⛄', k: 'snowman without snow' },
      { v: '☄️', k: 'comet' },
      { v: '💧', k: 'droplet' },
    ],
  },
  {
    id: 'vehicles', label: 'Транспорт', items: [
      { v: '🚗', k: 'машина автомобиль car' },
      { v: '🚕', k: 'такси taxi' },
      { v: '🚙', k: 'внедорожник suv' },
      { v: '🚌', k: 'автобус bus' },
      { v: '🏎️', k: 'гоночная машина race car' },
      { v: '🚓', k: 'полиция police car' },
      { v: '🚑', k: 'скорая ambulance' },
      { v: '🚒', k: 'пожарная fire truck' },
      { v: '🚚', k: 'грузовик truck' },
      { v: '🚲', k: 'велосипед bike bicycle' },
      { v: '✈️', k: 'самолет plane' },
      { v: '🚆', k: 'поезд train' },
      { v: '🧳', k: 'чемодан luggage' },
    ],
  },
  {
    id: 'objects', label: 'Предметы', items: [
      { v: '👓', k: 'glasses' },
      { v: '🕶️', k: 'sunglasses' },
      { v: '🥽', k: 'goggles' },
      { v: '🥼', k: 'lab coat' },
      { v: '🦺', k: 'safety vest' },
      { v: '👔', k: 'necktie' },
      { v: '👕', k: 't-shirt' },
      { v: '👖', k: 'jeans' },
      { v: '🧣', k: 'scarf' },
      { v: '🧤', k: 'gloves' },
      { v: '🧥', k: 'coat' },
      { v: '🧦', k: 'socks' },
      { v: '👗', k: 'dress' },
      { v: '👘', k: 'kimono' },
      { v: '🥻', k: 'sari' },
      { v: '🩱', k: 'one-piece swimsuit' },
      { v: '🩲', k: 'briefs' },
      { v: '🩳', k: 'shorts' },
      { v: '👙', k: 'bikini' },
      { v: '👚', k: 'woman’s clothes' },
      { v: '🪭', k: 'folding hand fan' },
      { v: '👛', k: 'purse' },
      { v: '👜', k: 'handbag' },
      { v: '👝', k: 'clutch bag' },
      { v: '🛍️', k: 'shopping bags' },
      { v: '🎒', k: 'backpack' },
      { v: '🩴', k: 'thong sandal' },
      { v: '👞', k: 'man’s shoe' },
      { v: '👟', k: 'running shoe' },
      { v: '🥾', k: 'hiking boot' },
      { v: '🥿', k: 'flat shoe' },
      { v: '👠', k: 'high-heeled shoe' },
      { v: '👡', k: 'woman’s sandal' },
      { v: '🩰', k: 'ballet shoes' },
      { v: '👢', k: 'woman’s boot' },
      { v: '🪮', k: 'hair pick' },
      { v: '👑', k: 'crown' },
      { v: '👒', k: 'woman’s hat' },
      { v: '🎩', k: 'top hat' },
      { v: '🎓', k: 'graduation cap' },
      { v: '🧢', k: 'billed cap' },
      { v: '🪖', k: 'military helmet' },
      { v: '⛑️', k: 'rescue worker’s helmet' },
      { v: '📿', k: 'prayer beads' },
      { v: '💄', k: 'lipstick' },
      { v: '💍', k: 'ring' },
      { v: '💎', k: 'gem stone' },
      { v: '🔇', k: 'muted speaker' },
      { v: '🔈', k: 'speaker low volume' },
      { v: '🔉', k: 'speaker medium volume' },
      { v: '🔊', k: 'speaker high volume' },
      { v: '📢', k: 'loudspeaker' },
      { v: '📣', k: 'megaphone' },
      { v: '📯', k: 'postal horn' },
      { v: '🔔', k: 'bell' },
      { v: '🔕', k: 'bell with slash' },
      { v: '🎼', k: 'musical score' },
      { v: '🎵', k: 'musical note' },
      { v: '🎶', k: 'musical notes' },
      { v: '🎙️', k: 'studio microphone' },
      { v: '🎚️', k: 'level slider' },
      { v: '🎛️', k: 'control knobs' },
      { v: '🎤', k: 'microphone' },
      { v: '🎧', k: 'headphone' },
      { v: '📻', k: 'radio' },
      { v: '🎷', k: 'saxophone' },
      { v: '🎺', k: 'trumpet' },
      { v: '🪗', k: 'accordion' },
      { v: '🎸', k: 'guitar' },
      { v: '🎹', k: 'musical keyboard' },
      { v: '🎻', k: 'violin' },
      { v: '🪕', k: 'banjo' },
      { v: '🥁', k: 'drum' },
      { v: '🪘', k: 'long drum' },
      { v: '🪇', k: 'maracas' },
      { v: '🪈', k: 'flute' },
      { v: '🪉', k: 'harp' },
      { v: '📱', k: 'mobile phone' },
      { v: '📲', k: 'mobile phone with arrow' },
      { v: '☎️', k: 'telephone' },
      { v: '📞', k: 'telephone receiver' },
      { v: '📟', k: 'pager' },
      { v: '📠', k: 'fax machine' },
      { v: '🔋', k: 'battery' },
      { v: '🪫', k: 'low battery' },
      { v: '🔌', k: 'electric plug' },
      { v: '💻', k: 'laptop' },
      { v: '🖥️', k: 'desktop computer' },
      { v: '🖨️', k: 'printer' },
      { v: '⌨️', k: 'keyboard' },
      { v: '🖱️', k: 'computer mouse' },
      { v: '🖲️', k: 'trackball' },
      { v: '💽', k: 'computer disk' },
      { v: '💾', k: 'floppy disk' },
      { v: '💿', k: 'optical disk' },
      { v: '📀', k: 'dvd' },
      { v: '🧮', k: 'abacus' },
      { v: '🎥', k: 'movie camera' },
      { v: '🎞️', k: 'film frames' },
      { v: '📽️', k: 'film projector' },
      { v: '🎬', k: 'clapper board' },
      { v: '📺', k: 'television' },
      { v: '📷', k: 'camera' },
      { v: '📸', k: 'camera with flash' },
      { v: '📹', k: 'video camera' },
      { v: '📼', k: 'videocassette' },
      { v: '🔍', k: 'magnifying glass tilted left' },
      { v: '🔎', k: 'magnifying glass tilted right' },
      { v: '🕯️', k: 'candle' },
      { v: '💡', k: 'light bulb' },
      { v: '🔦', k: 'flashlight' },
      { v: '🏮', k: 'red paper lantern' },
      { v: '🪔', k: 'diya lamp' },
      { v: '📔', k: 'notebook with decorative cover' },
      { v: '📕', k: 'closed book' },
      { v: '📖', k: 'open book' },
      { v: '📗', k: 'green book' },
      { v: '📘', k: 'blue book' },
      { v: '📙', k: 'orange book' },
      { v: '📚', k: 'books' },
      { v: '📓', k: 'notebook' },
      { v: '📒', k: 'ledger' },
      { v: '📃', k: 'page with curl' },
      { v: '📜', k: 'scroll' },
      { v: '📄', k: 'page facing up' },
      { v: '📰', k: 'newspaper' },
      { v: '🗞️', k: 'rolled-up newspaper' },
      { v: '📑', k: 'bookmark tabs' },
      { v: '🔖', k: 'bookmark' },
      { v: '🏷️', k: 'label' },
      { v: '🪙', k: 'coin' },
      { v: '💰', k: 'money bag' },
      { v: '💴', k: 'yen banknote' },
      { v: '💵', k: 'dollar banknote' },
      { v: '💶', k: 'euro banknote' },
      { v: '💷', k: 'pound banknote' },
      { v: '💸', k: 'money with wings' },
      { v: '💳', k: 'credit card' },
      { v: '🧾', k: 'receipt' },
      { v: '💹', k: 'chart increasing with yen' },
      { v: '✉️', k: 'envelope' },
      { v: '📧', k: 'e-mail' },
      { v: '📨', k: 'incoming envelope' },
      { v: '📩', k: 'envelope with arrow' },
      { v: '📤', k: 'outbox tray' },
      { v: '📥', k: 'inbox tray' },
      { v: '📦', k: 'package' },
      { v: '📫', k: 'closed mailbox with raised flag' },
      { v: '📪', k: 'closed mailbox with lowered flag' },
      { v: '📬', k: 'open mailbox with raised flag' },
      { v: '📭', k: 'open mailbox with lowered flag' },
      { v: '📮', k: 'postbox' },
      { v: '🗳️', k: 'ballot box with ballot' },
      { v: '✏️', k: 'pencil' },
      { v: '✒️', k: 'black nib' },
      { v: '🖋️', k: 'fountain pen' },
      { v: '🖊️', k: 'pen' },
      { v: '🖌️', k: 'paintbrush' },
      { v: '🖍️', k: 'crayon' },
      { v: '📝', k: 'memo' },
      { v: '💼', k: 'briefcase' },
      { v: '📁', k: 'file folder' },
      { v: '📂', k: 'open file folder' },
      { v: '🗂️', k: 'card index dividers' },
      { v: '📅', k: 'calendar' },
      { v: '📆', k: 'tear-off calendar' },
      { v: '🗒️', k: 'spiral notepad' },
      { v: '🗓️', k: 'spiral calendar' },
      { v: '📇', k: 'card index' },
      { v: '📈', k: 'chart increasing' },
      { v: '📉', k: 'chart decreasing' },
      { v: '📊', k: 'bar chart' },
      { v: '📋', k: 'clipboard' },
      { v: '📌', k: 'pushpin' },
      { v: '📍', k: 'round pushpin' },
      { v: '📎', k: 'paperclip' },
      { v: '🖇️', k: 'linked paperclips' },
      { v: '📏', k: 'straight ruler' },
      { v: '📐', k: 'triangular ruler' },
      { v: '✂️', k: 'scissors' },
      { v: '🗃️', k: 'card file box' },
      { v: '🗄️', k: 'file cabinet' },
      { v: '🗑️', k: 'wastebasket' },
      { v: '🔒', k: 'locked' },
      { v: '🔓', k: 'unlocked' },
      { v: '🔏', k: 'locked with pen' },
      { v: '🔐', k: 'locked with key' },
      { v: '🔑', k: 'key' },
      { v: '🗝️', k: 'old key' },
      { v: '🔨', k: 'hammer' },
      { v: '🪓', k: 'axe' },
      { v: '⛏️', k: 'pick' },
      { v: '⚒️', k: 'hammer and pick' },
      { v: '🛠️', k: 'hammer and wrench' },
      { v: '🗡️', k: 'dagger' },
      { v: '⚔️', k: 'crossed swords' },
      { v: '💣', k: 'bomb' },
      { v: '🪃', k: 'boomerang' },
      { v: '🏹', k: 'bow and arrow' },
      { v: '🛡️', k: 'shield' },
      { v: '🪚', k: 'carpentry saw' },
      { v: '🔧', k: 'wrench' },
      { v: '🪛', k: 'screwdriver' },
      { v: '🔩', k: 'nut and bolt' },
      { v: '⚙️', k: 'gear' },
      { v: '🗜️', k: 'clamp' },
      { v: '⚖️', k: 'balance scale' },
      { v: '🦯', k: 'white cane' },
      { v: '🔗', k: 'link' },
      { v: '⛓️‍💥', k: 'broken chain' },
      { v: '⛓️', k: 'chains' },
      { v: '🪝', k: 'hook' },
      { v: '🧰', k: 'toolbox' },
      { v: '🧲', k: 'magnet' },
      { v: '🪜', k: 'ladder' },
      { v: '🪏', k: 'shovel' },
      { v: '⚗️', k: 'alembic' },
      { v: '🧪', k: 'test tube' },
      { v: '🧫', k: 'petri dish' },
      { v: '🧬', k: 'dna' },
      { v: '🔬', k: 'microscope' },
      { v: '🔭', k: 'telescope' },
      { v: '📡', k: 'satellite antenna' },
      { v: '💉', k: 'syringe' },
      { v: '💊', k: 'pill' },
      { v: '🩹', k: 'adhesive bandage' },
      { v: '🩼', k: 'crutch' },
      { v: '🩺', k: 'stethoscope' },
      { v: '🩻', k: 'x-ray' },
      { v: '🚪', k: 'door' },
      { v: '🛗', k: 'elevator' },
      { v: '🪞', k: 'mirror' },
      { v: '🪟', k: 'window' },
      { v: '🛏️', k: 'bed' },
      { v: '🛋️', k: 'couch and lamp' },
      { v: '🪑', k: 'chair' },
      { v: '🚽', k: 'toilet' },
      { v: '🪠', k: 'plunger' },
      { v: '🚿', k: 'shower' },
      { v: '🛁', k: 'bathtub' },
      { v: '🪤', k: 'mouse trap' },
      { v: '🪒', k: 'razor' },
      { v: '🧴', k: 'lotion bottle' },
      { v: '🧷', k: 'safety pin' },
      { v: '🧹', k: 'broom' },
      { v: '🧺', k: 'basket' },
      { v: '🧻', k: 'roll of paper' },
      { v: '🪣', k: 'bucket' },
      { v: '🧼', k: 'soap' },
      { v: '🫧', k: 'bubbles' },
      { v: '🪥', k: 'toothbrush' },
      { v: '🧽', k: 'sponge' },
      { v: '🧯', k: 'fire extinguisher' },
      { v: '🛒', k: 'shopping cart' },
      { v: '🚬', k: 'cigarette' },
      { v: '⚰️', k: 'coffin' },
      { v: '🪦', k: 'headstone' },
      { v: '⚱️', k: 'funeral urn' },
      { v: '🧿', k: 'nazar amulet' },
      { v: '🪬', k: 'hamsa' },
      { v: '🗿', k: 'moai' },
      { v: '🪧', k: 'placard' },
      { v: '🪪', k: 'identification card' },
    ],
  },
  {
    id: 'symbols', label: 'Символы', items: [
      { v: '🏧', k: 'ATM sign' },
      { v: '🚮', k: 'litter in bin sign' },
      { v: '🚰', k: 'potable water' },
      { v: '♿', k: 'wheelchair symbol' },
      { v: '🚹', k: 'men’s room' },
      { v: '🚺', k: 'women’s room' },
      { v: '🚻', k: 'restroom' },
      { v: '🚼', k: 'baby symbol' },
      { v: '🚾', k: 'water closet' },
      { v: '🛂', k: 'passport control' },
      { v: '🛃', k: 'customs' },
      { v: '🛄', k: 'baggage claim' },
      { v: '🛅', k: 'left luggage' },
      { v: '⚠️', k: 'warning' },
      { v: '🚸', k: 'children crossing' },
      { v: '⛔', k: 'no entry' },
      { v: '🚫', k: 'prohibited' },
      { v: '🚳', k: 'no bicycles' },
      { v: '🚭', k: 'no smoking' },
      { v: '🚯', k: 'no littering' },
      { v: '🚱', k: 'non-potable water' },
      { v: '🚷', k: 'no pedestrians' },
      { v: '📵', k: 'no mobile phones' },
      { v: '🔞', k: 'no one under eighteen' },
      { v: '☢️', k: 'radioactive' },
      { v: '☣️', k: 'biohazard' },
      { v: '⬆️', k: 'up arrow' },
      { v: '↗️', k: 'up-right arrow' },
      { v: '➡️', k: 'right arrow' },
      { v: '↘️', k: 'down-right arrow' },
      { v: '⬇️', k: 'down arrow' },
      { v: '↙️', k: 'down-left arrow' },
      { v: '⬅️', k: 'left arrow' },
      { v: '↖️', k: 'up-left arrow' },
      { v: '↕️', k: 'up-down arrow' },
      { v: '↔️', k: 'left-right arrow' },
      { v: '↩️', k: 'right arrow curving left' },
      { v: '↪️', k: 'left arrow curving right' },
      { v: '⤴️', k: 'right arrow curving up' },
      { v: '⤵️', k: 'right arrow curving down' },
      { v: '🔃', k: 'clockwise vertical arrows' },
      { v: '🔄', k: 'counterclockwise arrows button' },
      { v: '🔙', k: 'BACK arrow' },
      { v: '🔚', k: 'END arrow' },
      { v: '🔛', k: 'ON! arrow' },
      { v: '🔜', k: 'SOON arrow' },
      { v: '🔝', k: 'TOP arrow' },
      { v: '🛐', k: 'place of worship' },
      { v: '⚛️', k: 'atom symbol' },
      { v: '🕉️', k: 'om' },
      { v: '✡️', k: 'star of David' },
      { v: '☸️', k: 'wheel of dharma' },
      { v: '☯️', k: 'yin yang' },
      { v: '✝️', k: 'latin cross' },
      { v: '☦️', k: 'orthodox cross' },
      { v: '☪️', k: 'star and crescent' },
      { v: '☮️', k: 'peace symbol' },
      { v: '🕎', k: 'menorah' },
      { v: '🔯', k: 'dotted six-pointed star' },
      { v: '🪯', k: 'khanda' },
      { v: '♈', k: 'Aries' },
      { v: '♉', k: 'Taurus' },
      { v: '♊', k: 'Gemini' },
      { v: '♋', k: 'Cancer' },
      { v: '♌', k: 'Leo' },
      { v: '♍', k: 'Virgo' },
      { v: '♎', k: 'Libra' },
      { v: '♏', k: 'Scorpio' },
      { v: '♐', k: 'Sagittarius' },
      { v: '♑', k: 'Capricorn' },
      { v: '♒', k: 'Aquarius' },
      { v: '♓', k: 'Pisces' },
      { v: '⛎', k: 'Ophiuchus' },
      { v: '🔀', k: 'shuffle tracks button' },
      { v: '🔁', k: 'repeat button' },
      { v: '🔂', k: 'repeat single button' },
      { v: '▶️', k: 'play button' },
      { v: '⏩', k: 'fast-forward button' },
      { v: '⏭️', k: 'next track button' },
      { v: '⏯️', k: 'play or pause button' },
      { v: '◀️', k: 'reverse button' },
      { v: '⏪', k: 'fast reverse button' },
      { v: '⏮️', k: 'last track button' },
      { v: '🔼', k: 'upwards button' },
      { v: '⏫', k: 'fast up button' },
      { v: '🔽', k: 'downwards button' },
      { v: '⏬', k: 'fast down button' },
      { v: '⏸️', k: 'pause button' },
      { v: '⏹️', k: 'stop button' },
      { v: '⏺️', k: 'record button' },
      { v: '⏏️', k: 'eject button' },
      { v: '🎦', k: 'cinema' },
      { v: '🔅', k: 'dim button' },
      { v: '🔆', k: 'bright button' },
      { v: '📶', k: 'antenna bars' },
      { v: '🛜', k: 'wireless' },
      { v: '📳', k: 'vibration mode' },
      { v: '📴', k: 'mobile phone off' },
      { v: '✖️', k: 'multiply' },
      { v: '➕', k: 'plus' },
      { v: '➖', k: 'minus' },
      { v: '➗', k: 'divide' },
      { v: '🟰', k: 'heavy equals sign' },
      { v: '♾️', k: 'infinity' },
      { v: '‼️', k: 'double exclamation mark' },
      { v: '⁉️', k: 'exclamation question mark' },
      { v: '❓', k: 'red question mark' },
      { v: '❔', k: 'white question mark' },
      { v: '❕', k: 'white exclamation mark' },
      { v: '❗', k: 'red exclamation mark' },
      { v: '〰️', k: 'wavy dash' },
      { v: '💱', k: 'currency exchange' },
      { v: '💲', k: 'heavy dollar sign' },
      { v: '♻️', k: 'recycling symbol' },
      { v: '⚜️', k: 'fleur-de-lis' },
      { v: '🔱', k: 'trident emblem' },
      { v: '📛', k: 'name badge' },
      { v: '🔰', k: 'Japanese symbol for beginner' },
      { v: '⭕', k: 'hollow red circle' },
      { v: '✅', k: 'check mark button' },
      { v: '☑️', k: 'check box with check' },
      { v: '✔️', k: 'check mark' },
      { v: '❌', k: 'cross mark' },
      { v: '❎', k: 'cross mark button' },
      { v: '➰', k: 'curly loop' },
      { v: '➿', k: 'double curly loop' },
      { v: '〽️', k: 'part alternation mark' },
      { v: '✳️', k: 'eight-spoked asterisk' },
      { v: '✴️', k: 'eight-pointed star' },
      { v: '❇️', k: 'sparkle' },
      { v: '©️', k: 'copyright' },
      { v: '®️', k: 'registered' },
      { v: '™️', k: 'trade mark' },
      { v: '🫟', k: 'splatter' },
      { v: '#️⃣', k: 'keycap #' },
      { v: '*️⃣', k: 'keycap *' },
      { v: '0️⃣', k: 'keycap 0' },
      { v: '1️⃣', k: 'keycap 1' },
      { v: '2️⃣', k: 'keycap 2' },
      { v: '3️⃣', k: 'keycap 3' },
      { v: '4️⃣', k: 'keycap 4' },
      { v: '5️⃣', k: 'keycap 5' },
      { v: '6️⃣', k: 'keycap 6' },
      { v: '7️⃣', k: 'keycap 7' },
      { v: '8️⃣', k: 'keycap 8' },
      { v: '9️⃣', k: 'keycap 9' },
      { v: '🔟', k: 'keycap 10' },
      { v: '🔠', k: 'input latin uppercase' },
      { v: '🔡', k: 'input latin lowercase' },
      { v: '🔢', k: 'input numbers' },
      { v: '🔣', k: 'input symbols' },
      { v: '🔤', k: 'input latin letters' },
      { v: '🅰️', k: 'A button (blood type)' },
      { v: '🆎', k: 'AB button (blood type)' },
      { v: '🅱️', k: 'B button (blood type)' },
      { v: '🆑', k: 'CL button' },
      { v: '🆒', k: 'COOL button' },
      { v: '🆓', k: 'FREE button' },
      { v: 'ℹ️', k: 'information' },
      { v: '🆔', k: 'ID button' },
      { v: 'Ⓜ️', k: 'circled M' },
      { v: '🆕', k: 'NEW button' },
      { v: '🆖', k: 'NG button' },
      { v: '🅾️', k: 'O button (blood type)' },
      { v: '🆗', k: 'OK button' },
      { v: '🅿️', k: 'P button' },
      { v: '🆘', k: 'SOS button' },
      { v: '🆙', k: 'UP! button' },
      { v: '🆚', k: 'VS button' },
      { v: '🈁', k: 'Japanese “here” button' },
      { v: '🈂️', k: 'Japanese “service charge” button' },
      { v: '🈷️', k: 'Japanese “monthly amount” button' },
      { v: '🈶', k: 'Japanese “not free of charge” button' },
      { v: '🈯', k: 'Japanese “reserved” button' },
      { v: '🉐', k: 'Japanese “bargain” button' },
      { v: '🈹', k: 'Japanese “discount” button' },
      { v: '🈚', k: 'Japanese “free of charge” button' },
      { v: '🈲', k: 'Japanese “prohibited” button' },
      { v: '🉑', k: 'Japanese “acceptable” button' },
      { v: '🈸', k: 'Japanese “application” button' },
      { v: '🈴', k: 'Japanese “passing grade” button' },
      { v: '🈳', k: 'Japanese “vacancy” button' },
      { v: '㊗️', k: 'Japanese “congratulations” button' },
      { v: '㊙️', k: 'Japanese “secret” button' },
      { v: '🈺', k: 'Japanese “open for business” button' },
      { v: '🈵', k: 'Japanese “no vacancy” button' },
      { v: '🔴', k: 'red circle' },
      { v: '🟠', k: 'orange circle' },
      { v: '🟡', k: 'yellow circle' },
      { v: '🟢', k: 'green circle' },
      { v: '🔵', k: 'blue circle' },
      { v: '🟣', k: 'purple circle' },
      { v: '🟤', k: 'brown circle' },
      { v: '⚫', k: 'black circle' },
      { v: '⚪', k: 'white circle' },
      { v: '🟥', k: 'red square' },
      { v: '🟧', k: 'orange square' },
      { v: '🟨', k: 'yellow square' },
      { v: '🟩', k: 'green square' },
      { v: '🟦', k: 'blue square' },
      { v: '🟪', k: 'purple square' },
      { v: '🟫', k: 'brown square' },
      { v: '⬛', k: 'black large square' },
      { v: '⬜', k: 'white large square' },
      { v: '◼️', k: 'black medium square' },
      { v: '◻️', k: 'white medium square' },
      { v: '◾', k: 'black medium-small square' },
      { v: '◽', k: 'white medium-small square' },
      { v: '▪️', k: 'black small square' },
      { v: '▫️', k: 'white small square' },
      { v: '🔶', k: 'large orange diamond' },
      { v: '🔷', k: 'large blue diamond' },
      { v: '🔸', k: 'small orange diamond' },
      { v: '🔹', k: 'small blue diamond' },
      { v: '🔺', k: 'red triangle pointed up' },
      { v: '🔻', k: 'red triangle pointed down' },
      { v: '💠', k: 'diamond with a dot' },
      { v: '🔘', k: 'radio button' },
      { v: '🔳', k: 'white square button' },
      { v: '🔲', k: 'black square button' },
    ],
  },
  {
    id: 'flags', label: 'Флаги', items: [
      { v: '🏁', k: 'chequered flag' },
      { v: '🚩', k: 'triangular flag' },
      { v: '🎌', k: 'crossed flags' },
      { v: '🏴', k: 'black flag' },
      { v: '🏳️', k: 'white flag' },
      { v: '🏴‍☠️', k: 'pirate flag' },
      { v: '🇦🇨', k: 'flag Ascension Island' },
      { v: '🇦🇩', k: 'flag Andorra' },
      { v: '🇦🇪', k: 'flag United Arab Emirates' },
      { v: '🇦🇫', k: 'flag Afghanistan' },
      { v: '🇦🇬', k: 'flag Antigua & Barbuda' },
      { v: '🇦🇮', k: 'flag Anguilla' },
      { v: '🇦🇱', k: 'flag Albania' },
      { v: '🇦🇲', k: 'flag Armenia' },
      { v: '🇦🇴', k: 'flag Angola' },
      { v: '🇦🇶', k: 'flag Antarctica' },
      { v: '🇦🇷', k: 'flag Argentina' },
      { v: '🇦🇸', k: 'flag American Samoa' },
      { v: '🇦🇹', k: 'flag Austria' },
      { v: '🇦🇺', k: 'flag Australia' },
      { v: '🇦🇼', k: 'flag Aruba' },
      { v: '🇦🇽', k: 'flag Åland Islands' },
      { v: '🇦🇿', k: 'flag Azerbaijan' },
      { v: '🇧🇦', k: 'flag Bosnia & Herzegovina' },
      { v: '🇧🇧', k: 'flag Barbados' },
      { v: '🇧🇩', k: 'flag Bangladesh' },
      { v: '🇧🇪', k: 'flag Belgium' },
      { v: '🇧🇫', k: 'flag Burkina Faso' },
      { v: '🇧🇬', k: 'flag Bulgaria' },
      { v: '🇧🇭', k: 'flag Bahrain' },
      { v: '🇧🇮', k: 'flag Burundi' },
      { v: '🇧🇯', k: 'flag Benin' },
      { v: '🇧🇱', k: 'flag St. Barthélemy' },
      { v: '🇧🇲', k: 'flag Bermuda' },
      { v: '🇧🇳', k: 'flag Brunei' },
      { v: '🇧🇴', k: 'flag Bolivia' },
      { v: '🇧🇶', k: 'flag Caribbean Netherlands' },
      { v: '🇧🇷', k: 'flag Brazil' },
      { v: '🇧🇸', k: 'flag Bahamas' },
      { v: '🇧🇹', k: 'flag Bhutan' },
      { v: '🇧🇻', k: 'flag Bouvet Island' },
      { v: '🇧🇼', k: 'flag Botswana' },
      { v: '🇧🇾', k: 'flag Belarus' },
      { v: '🇧🇿', k: 'flag Belize' },
      { v: '🇨🇦', k: 'flag Canada' },
      { v: '🇨🇨', k: 'flag Cocos (Keeling) Islands' },
      { v: '🇨🇩', k: 'flag Congo - Kinshasa' },
      { v: '🇨🇫', k: 'flag Central African Republic' },
      { v: '🇨🇬', k: 'flag Congo - Brazzaville' },
      { v: '🇨🇭', k: 'flag Switzerland' },
      { v: '🇨🇮', k: 'flag Côte d’Ivoire' },
      { v: '🇨🇰', k: 'flag Cook Islands' },
      { v: '🇨🇱', k: 'flag Chile' },
      { v: '🇨🇲', k: 'flag Cameroon' },
      { v: '🇨🇳', k: 'flag China' },
      { v: '🇨🇴', k: 'flag Colombia' },
      { v: '🇨🇵', k: 'flag Clipperton Island' },
      { v: '🇨🇶', k: 'flag Sark' },
      { v: '🇨🇷', k: 'flag Costa Rica' },
      { v: '🇨🇺', k: 'flag Cuba' },
      { v: '🇨🇻', k: 'flag Cape Verde' },
      { v: '🇨🇼', k: 'flag Curaçao' },
      { v: '🇨🇽', k: 'flag Christmas Island' },
      { v: '🇨🇾', k: 'flag Cyprus' },
      { v: '🇨🇿', k: 'flag Czechia' },
      { v: '🇩🇪', k: 'flag Germany' },
      { v: '🇩🇬', k: 'flag Diego Garcia' },
      { v: '🇩🇯', k: 'flag Djibouti' },
      { v: '🇩🇰', k: 'flag Denmark' },
      { v: '🇩🇲', k: 'flag Dominica' },
      { v: '🇩🇴', k: 'flag Dominican Republic' },
      { v: '🇩🇿', k: 'flag Algeria' },
      { v: '🇪🇦', k: 'flag Ceuta & Melilla' },
      { v: '🇪🇨', k: 'flag Ecuador' },
      { v: '🇪🇪', k: 'flag Estonia' },
      { v: '🇪🇬', k: 'flag Egypt' },
      { v: '🇪🇭', k: 'flag Western Sahara' },
      { v: '🇪🇷', k: 'flag Eritrea' },
      { v: '🇪🇸', k: 'flag Spain' },
      { v: '🇪🇹', k: 'flag Ethiopia' },
      { v: '🇪🇺', k: 'flag European Union' },
      { v: '🇫🇮', k: 'flag Finland' },
      { v: '🇫🇯', k: 'flag Fiji' },
      { v: '🇫🇰', k: 'flag Falkland Islands' },
      { v: '🇫🇲', k: 'flag Micronesia' },
      { v: '🇫🇴', k: 'flag Faroe Islands' },
      { v: '🇫🇷', k: 'flag France' },
      { v: '🇬🇦', k: 'flag Gabon' },
      { v: '🇬🇧', k: 'flag United Kingdom' },
      { v: '🇬🇩', k: 'flag Grenada' },
      { v: '🇬🇪', k: 'flag Georgia' },
      { v: '🇬🇫', k: 'flag French Guiana' },
      { v: '🇬🇬', k: 'flag Guernsey' },
      { v: '🇬🇭', k: 'flag Ghana' },
      { v: '🇬🇮', k: 'flag Gibraltar' },
      { v: '🇬🇱', k: 'flag Greenland' },
      { v: '🇬🇲', k: 'flag Gambia' },
      { v: '🇬🇳', k: 'flag Guinea' },
      { v: '🇬🇵', k: 'flag Guadeloupe' },
      { v: '🇬🇶', k: 'flag Equatorial Guinea' },
      { v: '🇬🇷', k: 'flag Greece' },
      { v: '🇬🇸', k: 'flag South Georgia & South Sandwich Islands' },
      { v: '🇬🇹', k: 'flag Guatemala' },
      { v: '🇬🇺', k: 'flag Guam' },
      { v: '🇬🇼', k: 'flag Guinea-Bissau' },
      { v: '🇬🇾', k: 'flag Guyana' },
      { v: '🇭🇰', k: 'flag Hong Kong SAR China' },
      { v: '🇭🇲', k: 'flag Heard & McDonald Islands' },
      { v: '🇭🇳', k: 'flag Honduras' },
      { v: '🇭🇷', k: 'flag Croatia' },
      { v: '🇭🇹', k: 'flag Haiti' },
      { v: '🇭🇺', k: 'flag Hungary' },
      { v: '🇮🇨', k: 'flag Canary Islands' },
      { v: '🇮🇩', k: 'flag Indonesia' },
      { v: '🇮🇪', k: 'flag Ireland' },
      { v: '🇮🇱', k: 'flag Israel' },
      { v: '🇮🇲', k: 'flag Isle of Man' },
      { v: '🇮🇳', k: 'flag India' },
      { v: '🇮🇴', k: 'flag British Indian Ocean Territory' },
      { v: '🇮🇶', k: 'flag Iraq' },
      { v: '🇮🇷', k: 'flag Iran' },
      { v: '🇮🇸', k: 'flag Iceland' },
      { v: '🇮🇹', k: 'flag Italy' },
      { v: '🇯🇪', k: 'flag Jersey' },
      { v: '🇯🇲', k: 'flag Jamaica' },
      { v: '🇯🇴', k: 'flag Jordan' },
      { v: '🇯🇵', k: 'flag Japan' },
      { v: '🇰🇪', k: 'flag Kenya' },
      { v: '🇰🇬', k: 'flag Kyrgyzstan' },
      { v: '🇰🇭', k: 'flag Cambodia' },
      { v: '🇰🇮', k: 'flag Kiribati' },
      { v: '🇰🇲', k: 'flag Comoros' },
      { v: '🇰🇳', k: 'flag St. Kitts & Nevis' },
      { v: '🇰🇵', k: 'flag North Korea' },
      { v: '🇰🇷', k: 'flag South Korea' },
      { v: '🇰🇼', k: 'flag Kuwait' },
      { v: '🇰🇾', k: 'flag Cayman Islands' },
      { v: '🇰🇿', k: 'flag Kazakhstan' },
      { v: '🇱🇦', k: 'flag Laos' },
      { v: '🇱🇧', k: 'flag Lebanon' },
      { v: '🇱🇨', k: 'flag St. Lucia' },
      { v: '🇱🇮', k: 'flag Liechtenstein' },
      { v: '🇱🇰', k: 'flag Sri Lanka' },
      { v: '🇱🇷', k: 'flag Liberia' },
      { v: '🇱🇸', k: 'flag Lesotho' },
      { v: '🇱🇹', k: 'flag Lithuania' },
      { v: '🇱🇺', k: 'flag Luxembourg' },
      { v: '🇱🇻', k: 'flag Latvia' },
      { v: '🇱🇾', k: 'flag Libya' },
      { v: '🇲🇦', k: 'flag Morocco' },
      { v: '🇲🇨', k: 'flag Monaco' },
      { v: '🇲🇩', k: 'flag Moldova' },
      { v: '🇲🇪', k: 'flag Montenegro' },
      { v: '🇲🇫', k: 'flag St. Martin' },
      { v: '🇲🇬', k: 'flag Madagascar' },
      { v: '🇲🇭', k: 'flag Marshall Islands' },
      { v: '🇲🇰', k: 'flag North Macedonia' },
      { v: '🇲🇱', k: 'flag Mali' },
      { v: '🇲🇲', k: 'flag Myanmar (Burma)' },
      { v: '🇲🇳', k: 'flag Mongolia' },
      { v: '🇲🇴', k: 'flag Macao SAR China' },
      { v: '🇲🇵', k: 'flag Northern Mariana Islands' },
      { v: '🇲🇶', k: 'flag Martinique' },
      { v: '🇲🇷', k: 'flag Mauritania' },
      { v: '🇲🇸', k: 'flag Montserrat' },
      { v: '🇲🇹', k: 'flag Malta' },
      { v: '🇲🇺', k: 'flag Mauritius' },
      { v: '🇲🇻', k: 'flag Maldives' },
      { v: '🇲🇼', k: 'flag Malawi' },
      { v: '🇲🇽', k: 'flag Mexico' },
      { v: '🇲🇾', k: 'flag Malaysia' },
      { v: '🇲🇿', k: 'flag Mozambique' },
      { v: '🇳🇦', k: 'flag Namibia' },
      { v: '🇳🇨', k: 'flag New Caledonia' },
      { v: '🇳🇪', k: 'flag Niger' },
      { v: '🇳🇫', k: 'flag Norfolk Island' },
      { v: '🇳🇬', k: 'flag Nigeria' },
      { v: '🇳🇮', k: 'flag Nicaragua' },
      { v: '🇳🇱', k: 'flag Netherlands' },
      { v: '🇳🇴', k: 'flag Norway' },
      { v: '🇳🇵', k: 'flag Nepal' },
      { v: '🇳🇷', k: 'flag Nauru' },
      { v: '🇳🇺', k: 'flag Niue' },
      { v: '🇳🇿', k: 'flag New Zealand' },
      { v: '🇴🇲', k: 'flag Oman' },
      { v: '🇵🇦', k: 'flag Panama' },
      { v: '🇵🇪', k: 'flag Peru' },
      { v: '🇵🇫', k: 'flag French Polynesia' },
      { v: '🇵🇬', k: 'flag Papua New Guinea' },
      { v: '🇵🇭', k: 'flag Philippines' },
      { v: '🇵🇰', k: 'flag Pakistan' },
      { v: '🇵🇱', k: 'flag Poland' },
      { v: '🇵🇲', k: 'flag St. Pierre & Miquelon' },
      { v: '🇵🇳', k: 'flag Pitcairn Islands' },
      { v: '🇵🇷', k: 'flag Puerto Rico' },
      { v: '🇵🇸', k: 'flag Palestinian Territories' },
      { v: '🇵🇹', k: 'flag Portugal' },
      { v: '🇵🇼', k: 'flag Palau' },
      { v: '🇵🇾', k: 'flag Paraguay' },
      { v: '🇶🇦', k: 'flag Qatar' },
      { v: '🇷🇪', k: 'flag Réunion' },
      { v: '🇷🇴', k: 'flag Romania' },
      { v: '🇷🇸', k: 'flag Serbia' },
      { v: '🇷🇺', k: 'flag Russia' },
      { v: '🇷🇼', k: 'flag Rwanda' },
      { v: '🇸🇦', k: 'flag Saudi Arabia' },
      { v: '🇸🇧', k: 'flag Solomon Islands' },
      { v: '🇸🇨', k: 'flag Seychelles' },
      { v: '🇸🇩', k: 'flag Sudan' },
      { v: '🇸🇪', k: 'flag Sweden' },
      { v: '🇸🇬', k: 'flag Singapore' },
      { v: '🇸🇭', k: 'flag St. Helena' },
      { v: '🇸🇮', k: 'flag Slovenia' },
      { v: '🇸🇯', k: 'flag Svalbard & Jan Mayen' },
      { v: '🇸🇰', k: 'flag Slovakia' },
      { v: '🇸🇱', k: 'flag Sierra Leone' },
      { v: '🇸🇲', k: 'flag San Marino' },
      { v: '🇸🇳', k: 'flag Senegal' },
      { v: '🇸🇴', k: 'flag Somalia' },
      { v: '🇸🇷', k: 'flag Suriname' },
      { v: '🇸🇸', k: 'flag South Sudan' },
      { v: '🇸🇹', k: 'flag São Tomé & Príncipe' },
      { v: '🇸🇻', k: 'flag El Salvador' },
      { v: '🇸🇽', k: 'flag Sint Maarten' },
      { v: '🇸🇾', k: 'flag Syria' },
      { v: '🇸🇿', k: 'flag Eswatini' },
      { v: '🇹🇦', k: 'flag Tristan da Cunha' },
      { v: '🇹🇨', k: 'flag Turks & Caicos Islands' },
      { v: '🇹🇩', k: 'flag Chad' },
      { v: '🇹🇫', k: 'flag French Southern Territories' },
      { v: '🇹🇬', k: 'flag Togo' },
      { v: '🇹🇭', k: 'flag Thailand' },
      { v: '🇹🇯', k: 'flag Tajikistan' },
      { v: '🇹🇰', k: 'flag Tokelau' },
      { v: '🇹🇱', k: 'flag Timor-Leste' },
      { v: '🇹🇲', k: 'flag Turkmenistan' },
      { v: '🇹🇳', k: 'flag Tunisia' },
      { v: '🇹🇴', k: 'flag Tonga' },
      { v: '🇹🇷', k: 'flag Türkiye' },
      { v: '🇹🇹', k: 'flag Trinidad & Tobago' },
      { v: '🇹🇻', k: 'flag Tuvalu' },
      { v: '🇹🇼', k: 'flag Taiwan' },
      { v: '🇹🇿', k: 'flag Tanzania' },
      { v: '🇺🇦', k: 'flag Ukraine' },
      { v: '🇺🇬', k: 'flag Uganda' },
      { v: '🇺🇲', k: 'flag U.S. Outlying Islands' },
      { v: '🇺🇳', k: 'flag United Nations' },
      { v: '🇺🇸', k: 'flag United States' },
      { v: '🇺🇾', k: 'flag Uruguay' },
      { v: '🇺🇿', k: 'flag Uzbekistan' },
      { v: '🇻🇦', k: 'flag Vatican City' },
      { v: '🇻🇨', k: 'flag St. Vincent & Grenadines' },
      { v: '🇻🇪', k: 'flag Venezuela' },
      { v: '🇻🇬', k: 'flag British Virgin Islands' },
      { v: '🇻🇮', k: 'flag U.S. Virgin Islands' },
      { v: '🇻🇳', k: 'flag Vietnam' },
      { v: '🇻🇺', k: 'flag Vanuatu' },
      { v: '🇼🇫', k: 'flag Wallis & Futuna' },
      { v: '🇼🇸', k: 'flag Samoa' },
      { v: '🇽🇰', k: 'flag Kosovo' },
      { v: '🇾🇪', k: 'flag Yemen' },
      { v: '🇾🇹', k: 'flag Mayotte' },
      { v: '🇿🇦', k: 'flag South Africa' },
      { v: '🇿🇲', k: 'flag Zambia' },
      { v: '🇿🇼', k: 'flag Zimbabwe' },
      { v: '🏴󠁧󠁢󠁥󠁮󠁧󠁿', k: 'flag England' },
      { v: '🏴󠁧󠁢󠁳󠁣󠁴󠁿', k: 'flag Scotland' },
      { v: '🏴󠁧󠁢󠁷󠁬󠁳󠁿', k: 'flag Wales' },
    ],
  },
];

// Flat, deduplicated list — used by the plain grid picker (reactions) and as the
// fallback "search across everything" source for the categorized picker.
export const ALL_EMOJI_ITEMS: EmojiItem[] = (() => {
  const seen = new Set<string>();
  const items: EmojiItem[] = [];
  for (const cat of EMOJI_CATEGORIES) {
    for (const item of cat.items) {
      if (seen.has(item.v)) continue;
      seen.add(item.v);
      items.push(item);
    }
  }
  return items;
})();

export function searchEmoji(query: string, source: EmojiItem[] = ALL_EMOJI_ITEMS): EmojiItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return source;
  return source.filter(i => i.v.includes(q) || i.k.toLowerCase().includes(q));
}

// Word → emoji index for the composer's "suggest as you type" popup — keyed on
// exact whole-word matches (not substrings, unlike searchEmoji above), so typing
// "смех" mid-sentence suggests 😂/🤣 the moment that whole word is complete, without
// firing on partial words like "смехотвор...".
const WORD_INDEX: Map<string, EmojiItem[]> = (() => {
  const map = new Map<string, EmojiItem[]>();
  for (const item of ALL_EMOJI_ITEMS) {
    for (const word of item.k.toLowerCase().split(/\s+/)) {
      if (!word) continue;
      const list = map.get(word) ?? [];
      list.push(item);
      map.set(word, list);
    }
  }
  return map;
})();

// All distinct keywords, longest first — used by the fuzzy pass below so a shorter
// keyword's suggestions don't crowd out a longer, more-specific one that also
// matches (e.g. typing "смеш" should prioritize "смешно" over any short unrelated
// word that happens to also be ≥70% covered).
const ALL_WORDS: string[] = Array.from(WORD_INDEX.keys()).sort((a, b) => b.length - a.length);

export function matchEmojiByWord(word: string): EmojiItem[] {
  const w = word.trim().toLowerCase();
  if (w.length < 2) return [];

  const exact = WORD_INDEX.get(w);
  if (exact) return exact;

  // Fuzzy pass: suggest while still typing, once what's typed so far is a prefix of
  // a real keyword AND covers at least 70% of that keyword's length — e.g. "печата"
  // (6 chars) covers 6/7 ≈ 86% of "печатаю", so the suggestion appears before the
  // word is even finished. A word can match several keywords (returns all of them,
  // deduplicated by emoji).
  const seen = new Set<string>();
  const results: EmojiItem[] = [];
  for (const candidate of ALL_WORDS) {
    if (candidate.length < w.length) continue; // typed text can't out-cover a shorter real word
    if (w.length / candidate.length < 0.7) continue;
    if (!candidate.startsWith(w)) continue;
    for (const item of WORD_INDEX.get(candidate) ?? []) {
      if (seen.has(item.v)) continue;
      seen.add(item.v);
      results.push(item);
    }
  }
  return results;
}
